import { baseCamera2DData, baseCamera3DData, baseLayers, emptyClientInfo } from '@baseValues'
import { MorphDictionary } from '@components/Sidebar/Hole/HolePropertyInput'
import { getGLBData } from '@proxies/getGLBData'
import { getTexture } from '@proxies/getTexture'
import { getUnidadAgrupada } from '@proxies/getUnidadAgrupada'
import { openProyect } from '@proxies/openProyect'
import { setTextos, t } from '@proxies/translation'
import { setResource } from '@store'
import {
  AnyID,
  BackOpenProject,
  DesignerInfoPayload,
  Getter,
  Hole,
  Item,
  ItemGroup,
  ItemGroupID,
  LinkedItem,
  Modes,
  Room,
  savedView,
  Setter,
  Store,
  View,
  Walls,
  Zone,
} from '@types'
import { calculateDeltasBetweenSelectedItems } from '@utils/calcDeltaItems'
import getStandardMaterial from '@utils/conversion/getStandardMaterial'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { radians } from '@utils/generalMaths/geometry'
import isHole from '@utils/sceneObjects/assertions/isHole'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { computeItemGroupOBB } from '@utils/sceneObjects/computeItemGroupOBB'
import getId from '@utils/sceneObjects/getId'
import { getZonesFromGLB } from '@utils/sceneObjects/getZonesFromGLB'
import {
  getFreeDimensionAltoGlbMorphMm,
  getFreeDimensionStoreAlto,
  isFreeDimensionDimensionMorph,
  itemUsesFreeDimensionAltoGlbMorph,
  clampMorphMm,
  isFreeDimensionAnchoApiMorphCatalog,
  itemUsesFreeDimensionAltoApiMorph,
  itemUsesFreeDimensionAnchoGlbMorph,
  itemUsesFreeDimensionFondoGlbMorph,
  logFreeDimensionAncho,
  logFreeDimensionFondo,
  usesFreeDimensionScaling,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { purgeSceneGlbForCatalog } from '@utils/sceneObjects/purgeSceneGlbForCatalog'
import { removeLinkedHierarchyFromStore } from '@utils/sceneObjects/removeLinkedHierarchyFromStore'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import { getWallCOBB } from '@utils/sceneObjects/walls/getWallCOBB'
import { getInitialToolbarMenuColumns } from '@utils/toolbarMenuColumns'
import SuperJSON from 'superjson'
import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

export type ProjectActions = ReturnType<typeof createProjectActions>

export const createProjectActions = (set: Setter, get: Getter) => ({
  setMode: (mode: Modes) =>
    set((store) => {
      store.mode = mode
      // Check global: desactivar isOutlineMode solo al cambiar a modo 3D (excepto alzados)
      if (mode === 'MODE_3D_VIEW' || mode === 'MODE_DRAGGING_ITEM_3D' || mode === 'MODE_FORCE_3D') {
        store.isOutlineMode = false
      }
    }),
  setSelectedRoom: (room: Room) =>
    set((store) => {
      if (!room) return console.warn('Room not provided')
      store.selectedRoom = room
    }),

  setPreference: <T extends keyof Store['prefs']>(name: T, value: Store['prefs'][T]) => {
    set((store) => {
      const prefs = store.prefs
      prefs[name] = value
    })
  },
  editPrice: (price: number) => {
    set((store) => {
      store.price += price
    })
  },
  calculatePrice: () => {
    set((store) => {
      const items = store.layers[store.currentLayer].items
      const newPrice = Array.from(items).reduce((acc, el) => {
        const item = store.items.get(el) as { price: number }

        if (!item) return acc
        const itemPrice = item?.price
        if (!itemPrice) return acc
        return (acc += itemPrice)
      }, 0)
      store.price = newPrice
    })
  },
  setLanguageOptions: (options: Store['textos']['idioma'][]) => {
    //TODO: check what the back gives us
    set((store) => {
      store.languageOptions = options
    })
  },
  setCameraMove: (newState?: boolean) => {
    set((store) => {
      store.shouldCameraMove = newState !== undefined ? newState : !store.shouldCameraMove
    })
  },
  setCanDraw: (bool: boolean) => {
    set((store) => {
      store.canDraw = bool
    })
  },
  t: (text: keyof Store['textos']['textos']) => t(set, get, text),
  setTextos: (textos: Store['textos']) => {
    setTextos(get, set, textos)
  },
  setIdiomaCultura: (id: string) => {
    set((store) => {
      store.idiomaCultura = id
    })
  },
  setClientInfo: (clientInfo: Store['clientInfo']) => {
    set((store) => {
      store.clientInfo = {
        ...clientInfo,
      }
    })
  },
  setPendingDesignerInfo: (payload: DesignerInfoPayload | null) => {
    set((store) => {
      store.pendingDesignerInfo = payload
    })
  },
  setPrefs: (prefs: Store['prefs'], prefsInfo: Store['prefsInfo']) => {
    set((store) => {
      store.prefsInfo = prefsInfo
      const normalized = { ...prefs } as Store['prefs']
      normalized.COLUMNAMENU = getInitialToolbarMenuColumns(
        normalized as unknown as Record<string, unknown>,
      )
      store.prefs = normalized
      setResource('prefs', true)
    })
  },
  setTextures: async <S extends string, N extends number>(
    catalogo: S,
    unidadsuelo: N,
    unidadpared0: N,
    unidadpared1: N,
    unidadtecho: N,
    unidadtabique0: N,
    unidadtabique1: N,
    unidadtabique2: N,
    unidadtabique3: N,
    unidadtabique4: N,
    unidadtabique5: N,
    token?: string,
  ) => {
    // prefs?.CATALOGOUNIVERSAL &&
    //       prefs?.UNIDADSUELO &&
    //       prefs?.UNIDADPARED0 &&
    //       prefs?.UNIDADPARED1 &&
    //       prefs?.UNIDADTECHO
    const catalog = get().prefs['CATALOGOUNIVERSAL'] ?? catalogo
    const isLogged = window.localStorage.getItem('isUserLogged')
    const texture1 = await getTexture(catalog, unidadsuelo, token)
    const texture2 = await getTexture(catalog, unidadpared0, token)
    const texture3 = await getTexture(catalog, unidadpared1, token)
    const texture4 = await getTexture(catalog, unidadtecho, token)
    const texture5 = await getTexture(catalog, unidadtabique0, token)
    const texture6 = await getTexture(catalog, unidadtabique1, token)
    const texture7 = await getTexture(catalog, unidadtabique2, token)
    const texture8 = await getTexture(catalog, unidadtabique3, token)
    const texture9 = await getTexture(catalog, unidadtabique4, token)
    const texture10 = await getTexture(catalog, unidadtabique5, token)

    if (
      !texture1 ||
      !texture2 ||
      !texture3 ||
      !texture4 ||
      !texture5 ||
      !texture6 ||
      !texture7 ||
      !texture8 ||
      !texture9 ||
      !texture10
    )
      return

    texture2.name = 'MSG_94'
    texture2.id = 'textureA'
    texture3.name = 'MSG_95'
    texture3.id = 'textureB'
    texture1.id = 'areaSuelo'
    texture1.name = 'MSG_182'
    texture4.id = 'areaTecho'
    texture4.name = 'MSG_221'
    texture5.id = 'tabiqueCara0'
    texture5.name = 'MSG_221'
    texture6.id = 'tabiqueCara1'
    texture6.name = 'MSG_221'
    texture7.id = 'tabiqueCara2'
    texture7.name = 'MSG_221'
    texture8.id = 'tabiqueCara3'
    texture8.name = 'MSG_221'
    texture9.id = 'tabiqueCara4'
    texture9.name = 'MSG_221'
    texture10.id = 'tabiqueCara5'
    texture10.name = 'MSG_221'

    const items = new Map()
    const areas = new Map()

    set((store) => {
      store.texture1 = texture1
      store.texture2 = texture2
      store.texture3 = texture3
      store.texture4 = texture4
      store.texture5 = texture5
      store.texture6 = texture6
      store.texture7 = texture7
      store.texture8 = texture8
      store.texture9 = texture9
      store.texture10 = texture10
      if (!isLogged) {
        store.items = items
        store.areas = areas
      }
      setResource('texturas', true)
    })
  },
  resetTextures: async <S extends string, N extends number>(
    catalogo: S,
    unidadsuelo: N,
    unidadpared0: N,
    unidadpared1: N,
    unidadtecho: N,
    unidadtabique0: N,
    unidadtabique1: N,
    unidadtabique2: N,
    unidadtabique3: N,
    unidadtabique4: N,
    unidadtabique5: N,
  ) => {
    const texture1 = await getTexture(catalogo, unidadsuelo)
    const texture2 = await getTexture(catalogo, unidadpared0)
    const texture3 = await getTexture(catalogo, unidadpared1)
    const texture4 = await getTexture(catalogo, unidadtecho)
    const texture5 = await getTexture(catalogo, unidadtabique0)
    const texture6 = await getTexture(catalogo, unidadtabique1)
    const texture7 = await getTexture(catalogo, unidadtabique2)
    const texture8 = await getTexture(catalogo, unidadtabique3)
    const texture9 = await getTexture(catalogo, unidadtabique4)
    const texture10 = await getTexture(catalogo, unidadtabique5)
    if (!texture1 || !texture2 || !texture3 || !texture4) return

    texture2.name = 'MSG_94'
    texture2.id = 'textureA'
    texture3.name = 'MSG_95'
    texture3.id = 'textureB'
    texture1.id = 'areaSuelo'
    texture1.name = 'MSG_182'
    texture4.id = 'areaTecho'
    texture4.name = 'MSG_221'
    texture5.id = 'tabiqueCara0'
    texture5.name = 'MSG_221'
    texture6.id = 'tabiqueCara1'
    texture6.name = 'MSG_221'
    texture7.id = 'tabiqueCara2'
    texture7.name = 'MSG_221'
    texture8.id = 'tabiqueCara3'
    texture8.name = 'MSG_221'
    texture9.id = 'tabiqueCara4'
    texture9.name = 'MSG_221'
    texture10.id = 'tabiqueCara5'
    texture10.name = 'MSG_221'

    set((store) => {
      store.texture1 = getStandardMaterial(texture1)
      store.texture2 = getStandardMaterial(texture2)
      store.texture3 = getStandardMaterial(texture3)
      store.texture4 = getStandardMaterial(texture4)
      store.texture5 = getStandardMaterial(texture5)
      store.texture6 = getStandardMaterial(texture6)
      store.texture7 = getStandardMaterial(texture7)
      store.texture8 = getStandardMaterial(texture8)
      store.texture9 = getStandardMaterial(texture9)
      store.texture10 = getStandardMaterial(texture10)
      setResource('texturas', true)
    })
  },

  setCamera3D: (data: Store['camera3DData']) => {
    set((store) => {
      store.camera3DData = data
    })
  },
  setCamera2DPosition: (position: Store['camera2DPosition']) => {
    set((store) => {
      store.camera2DPosition = position
    })
  },
  setLastView: (lastView: Store['lastView']) => {
    set((store) => {
      store.lastView = lastView
    })
  },
  addSavedView: (newView: savedView) => {
    set((store) => ({
      savedViews: [...(store.savedViews || []), newView],
    }))
  },
  deleteSavedView: (index: number) => {
    set((store) => ({
      savedViews: store.savedViews.filter((_, i) => i !== index),
    }))
  },
  setSavedViews: (views: View[]) => {
    set(() => ({
      savedViews: views,
    }))
  },
  setStoreFromBack: async (
    id: number,
    tmp = false,
    comesFromStances = false,
    projectInfo?: DesignerInfoPayload | null,
  ) => {
    const fallbackDesignerInfo = get().pendingDesignerInfo ?? null
    const designerInfo = projectInfo ?? fallbackDesignerInfo ?? null

    const backResponse = await openProyect(id, tmp)

    // Resetear estado del modal de unidades para nuevos proyectos
    set((store) => {
      store.modalUnidadesMostrado = false
      store.unidadesFaltantes = []
      store.mostrarModalUnidadesFaltantes = false
    })

    // Validar unidades del JSON contra el catálogo disponible
    const validateUnidades = (jsonString: string, catalogoUnidades: any[], t: any) => {
      try {
        // El jsonString ya viene como string del backResponse.json
        const json = JSON.parse(jsonString)
        const unidadesEnJson = new Set()

        // Función recursiva para buscar todos los idUnidades en cualquier parte del JSON
        const buscarIdUnidadesEnObjeto = (obj: any, path: string = '') => {
          if (typeof obj !== 'object' || obj === null) return

          // Si el objeto tiene idUnidades Y es de tipo linkedItem, agregarlo
          if (obj.idUnidades !== undefined && obj.type === 'linkedItem') {
            unidadesEnJson.add(obj.idUnidades)
          }

          // Recorrer todas las propiedades del objeto
          for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
              const value = obj[key]
              const newPath = path ? `${path}.${key}` : key

              if (typeof value === 'object' && value !== null) {
                if (Array.isArray(value)) {
                  // Si es un array, recorrer cada elemento
                  value.forEach((item, index) => {
                    buscarIdUnidadesEnObjeto(item, `${newPath}[${index}]`)
                  })
                } else {
                  // Si es un objeto, recorrerlo recursivamente
                  buscarIdUnidadesEnObjeto(value, newPath)
                }
              }
            }
          }
        }

        buscarIdUnidadesEnObjeto(json, 'json')

        // Convertir catálogo a Set para fácil búsqueda
        const catalogoUnidadesSet = new Set(catalogoUnidades.map((u) => u.idUnidades))

        // Encontrar unidades faltantes
        const unidadesFaltantes = Array.from(unidadesEnJson).filter(
          (id) => !catalogoUnidadesSet.has(id),
        )

        if (unidadesFaltantes.length > 0) {
          // Obtener detalles de las unidades faltantes
          const detallesFaltantes = unidadesFaltantes.map((id) => {
            // Buscar en el JSON para obtener referencia si existe
            let referencia = 'Desconocida'

            const buscarReferencia = (obj: any) => {
              if (typeof obj !== 'object' || obj === null) return

              if (obj.idUnidades === id && obj.referencia) {
                referencia = obj.referencia
                return true
              }

              for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                  const value = obj[key]
                  if (typeof value === 'object' && value !== null) {
                    if (Array.isArray(value)) {
                      for (const item of value) {
                        if (buscarReferencia(item)) return true
                      }
                    } else {
                      if (buscarReferencia(value)) return true
                    }
                  }
                }
              }
              return false
            }

            buscarReferencia(json)

            if (referencia === 'Desconocida') {
              console.log(`   ❌ No se encontró referencia para ID: ${id}`)
            }

            return { idUnidades: id, referencia }
          })

          // Guardar las unidades faltantes en el store para que el componente React las muestre después del spinner
          set((store) => {
            store.unidadesFaltantes = detallesFaltantes
            // No mostrar el modal aquí, se mostrará después del setTimeout
          })

          return { tieneFaltantes: true, unidades: detallesFaltantes }
        }

        return { tieneFaltantes: false, unidades: [] }
      } catch (error) {
        console.error('Error validando unidades:', error)
        return { tieneFaltantes: false, unidades: [] }
      }
    }

    const info = SuperJSON.parse(backResponse.json) as BackOpenProject
    const loader = setGLTFLoader()
    const fetchedGLB = new Set()

    set((store) => {
      store.sceneGLB = new Map()
      store.sceneBoundingBoxes = new Map()
    })

    for (const itemData of info.items.values()) {
      const toSearch = itemData.catalogID || itemData.idUnidades
      if (fetchedGLB.has(toSearch)) continue
      fetchedGLB.add(toSearch)

      if (itemData.glbPath && toSearch) {
        const glb = await loader.loadAsync(itemData.glbPath)
        const glbData = await getGLBData(toSearch)

        set((store) => {
          if (store.sceneGLB.has(toSearch)) return
          if (itemData.type === 'holes') {
            store.sceneGLB.set(toSearch, {
              glb: glb,
              backGLBInfo: glbData,
            })
            return
          }
          store.sceneGLB.set(toSearch, {
            glb: glb,
            backGLBInfo: glbData,
          })
        })
      }
    }
    const items = new Map()
    info.items?.forEach((item) => {
      // TODO: partitions
      if (item.type === 'walls') {
        if (item.perpendicularPoint) {
          const { x, y, z } = item.perpendicularPoint
          const pp = toVector3([x, y, z])
          const { x: _x, y: _y, z: _z } = item.symmetricalPerpendicularPoint
          const spp = toVector3([_x, _y, _z])
          items.set(item.id, {
            ...item,
            perpendicularPoint: pp,
            symmetricalPerpendicularPoint: spp,
          })
          return
        }
      }
      if (item?.zones) {
        const itemCopy = { ...item }
        for (const zoneName in itemCopy.zones) {
          const zone = { ...itemCopy.zones[zoneName] }
          // Check if the zone has a valid position and rotation
          if (zone.position && zone.rotation) {
            const fixedPosition = toVector3([zone.position.x, zone.position.y, zone.position.z])
            const fixedRotation = new Euler(
              zone.rotation._x,
              zone.rotation._y,
              zone.rotation._z,
              zone.rotation._order,
            )
            const fixedScale = toVector3([zone.scale.x, zone.scale.y, zone.scale.z])
            zone.scale = fixedScale
            zone.position = fixedPosition
            zone.rotation = fixedRotation
          }
          // Handle other textures of the zone if needed
          // For example, you can access zone.scale or other textures here
        }
        items.set(item.id, itemCopy)
      } else {
        items.set(item.id, item)
      }
      if (item?.matrix) {
        const itemCopy = { ...item }
        const fixedMatrix = new Matrix4().fromArray(item.matrix.elements)
        itemCopy.matrix = fixedMatrix
        items.set(item.id, itemCopy)
      } else {
        items.set(item.id, item)
      }
    })
    const sceneBoundingBoxes = new Map()
    for (const [key, value] of info.sceneBoundingBoxes.entries()) {
      const item = items.get(key)
      // if (!item) continue
      const type = value.type
      const { x, y, z } = value.boundingBox.center
      const { x: sizeX, y: sizeY, z: sizeZ } = value.boundingBox.halfSize
      if (key.startsWith('COBB')) {
        const finalKey = key.replace('COBB_', '')
        const actualItem = items.get(finalKey)
        if (!actualItem) continue
        const rotation = actualItem.rotation
        const boundingBox = new OBB(toVector3([x, y, z]), toVector3([sizeX, sizeY, sizeZ]))
        const collisionOBB = new OBB(
          toVector3([x, 1, z]),
          toVector3([0, actualItem.height, actualItem.thickness / 2]),
        )
        const mat4 = new Matrix4().makeRotationFromEuler(
          new Euler(0, -rotation + 2 * Math.PI, 0, 'XYZ'),
        )
        const rot = new Matrix3().setFromMatrix4(mat4)
        boundingBox.rotation = rot
        sceneBoundingBoxes.set(key, { type, boundingBox: collisionOBB })
        continue
      }
      if (!item) continue
      const boundingBox = new OBB(toVector3([x, y, z]), toVector3([sizeX, sizeY, sizeZ]))
      sceneBoundingBoxes.set(key, { type, boundingBox })
    }
    set((store) => {
      store.items = items
      store.areas = info.areas
      store.layers = info.layers
      store.price = info.price ?? 0
      store.storeHistory = []
      store.layers[store.currentLayer].selected.clear()
      store.canDraw = true
      store.currentArea = ''
      store.currentFirstWall = ''
      store.currentLayer = info.currLayer
      store.currentObjectID = ''
      store.mode = 'MODE_IDLE'
      store.sceneBoundingBoxes = sceneBoundingBoxes
      if (!comesFromStances) {
        const disenadorActual =
          designerInfo?.disenadorActual ?? backResponse.disenyo.disenadorActual ?? null
        const disenadorOriginal =
          designerInfo?.disenadorOriginal ?? backResponse.disenyo.disenadorOriginal ?? null
        const numProyectoTpc =
          designerInfo?.numProyectoTpc ?? backResponse.disenyo.numProyectoTpc ?? null

        store.clientInfo = {
          nombreProyecto: backResponse.disenyo.nombreProyecto,
          descripcion: backResponse.disenyo.descripcion,
          nombre: backResponse.disenyo.nombre,
          apellidos: backResponse.disenyo.apellidos,
          direccion: backResponse.disenyo.direccion,
          codPostal: backResponse.disenyo.codPostal,
          poblacion: backResponse.disenyo.poblacion,
          email: backResponse.disenyo.email,
          pais: backResponse.disenyo.pais,
          movil: backResponse.disenyo.movil?.toString?.() ?? '',
          numProyectoTpc: numProyectoTpc ?? undefined,
          idDisenos: backResponse.disenyo.idDisenos,
          idVersiones: backResponse.disenyo.idVersiones,
          idImpuestos: backResponse.disenyo.idImpuestos ?? 0,
          disenadorActual,
          disenadorOriginal,
        }
      } else {
        store.clientInfo = {
          ...emptyClientInfo,
          nombreProyecto: backResponse.disenyo.nombreProyecto,
          numProyectoTpc: backResponse.disenyo.numProyectoTpc ?? undefined,
          disenadorActual: backResponse.disenyo.disenadorActual ?? null,
          disenadorOriginal: backResponse.disenyo.disenadorOriginal ?? null,
          movil: backResponse.disenyo.movil?.toString?.() ?? '',
        }
      }
      store.collisionableItems = info.collisionableItems ?? new Set()
      store.savedViews = info.savedViews || []
      store.esMio = backResponse.esMio ?? true
      store.pendingDesignerInfo = null
    })

    // Validar unidades después de cargar todo (después del spinner)
    if (backResponse && backResponse.json && backResponse.unidades) {
      const t = get().t
      validateUnidades(backResponse.json, backResponse.unidades, t)
    }
  },
  addProjectItemsToCurrentScene: async (idVersiones: number) => {
    try {
      const start = performance.now()
      // Primero intentamos con tmp = false (carga completa, como setStoreFromBack)
      // Si el back no devuelve json en este modo (caso layouts/favoritos), hacemos
      // un fallback automático a tmp = true para mantener compatibilidad.
      let backResponse = await openProyect(idVersiones, false)
      const afterOpen = performance.now()

      // Fallback: si no hay json en modo completo, reintentar en modo tmp (true)
      if (!backResponse || !backResponse.json) {
        backResponse = await openProyect(idVersiones, true)
      }

      // Validar que backResponse existe y tiene la propiedad json
      if (!backResponse || !backResponse.json) {
        console.error(
          '❌ No se pudo obtener la respuesta del proyecto:',
          idVersiones,
          'Respuesta:',
          backResponse,
        )
        return false
      }

      const info = SuperJSON.parse(backResponse.json) as BackOpenProject

      // Validar que info tiene items
      if (!info || !info.items || info.items.size === 0) {
        console.error('❌ El proyecto no tiene items o está vacío:', idVersiones)
        return false
      }

      const loader = setGLTFLoader()
      const fetchedGLB = new Set()
      const currentStore = get()

      // Cargar GLBs necesarios sin limpiar los existentes
      const glbStart = performance.now()
      for (const itemData of info.items.values()) {
        const toSearch = itemData.catalogID || itemData.idUnidades
        if (fetchedGLB.has(toSearch)) continue
        fetchedGLB.add(toSearch)

        // Solo cargar si no está ya en la escena
        if (!currentStore.sceneGLB.has(toSearch) && itemData.glbPath && toSearch) {
          const glb = await loader.loadAsync(itemData.glbPath)
          const glbData = await getGLBData(toSearch)

          set((store) => {
            if (store.sceneGLB.has(toSearch)) return
            if (itemData.type === 'holes') {
              store.sceneGLB.set(toSearch, {
                glb: glb,
                backGLBInfo: glbData,
              })
              return
            }
            store.sceneGLB.set(toSearch, {
              glb: glb,
              backGLBInfo: glbData,
            })
          })
        }
      }

      // Procesar items del proyecto y agregarlos al proyecto actual
      const newItems = new Map()
      const idMapping = new Map<string, string>() // Mapeo de IDs antiguos a nuevos
      const currentIncrement = get().increment
      let newIncrement = currentIncrement

      // Primero, crear mapeo de IDs y procesar todos los items
      const mapStart = performance.now()
      info.items?.forEach((item) => {
        // Generar nuevos IDs para evitar conflictos
        let newId: string
        if (item.type === 'walls') {
          newId = getId('walls')
        } else if (item.type === 'holes') {
          newId = getId('holes')
        } else if (item.type === 'linkedItem') {
          newId = getId('linkedItem')
        } else if (item.type === 'linkedSon') {
          newId = getId('linkedSon')
        } else if (item.type === 'items') {
          newId = getId('items')
        } else {
          newId = getId('items')
        }

        idMapping.set(item.id, newId)
      })

      // Ahora procesar items con los nuevos IDs
      const processItemsStart = performance.now()
      info.items?.forEach((item) => {
        const newId = idMapping.get(item.id)!
        if (!newId) return

        // Procesar item manteniendo su estructura
        let processedItem: any = { ...item, id: newId }

        // Procesar walls
        if (item.type === 'walls') {
          if (item.perpendicularPoint) {
            const { x, y, z } = item.perpendicularPoint
            const pp = toVector3([x, y, z])
            const { x: _x, y: _y, z: _z } = item.symmetricalPerpendicularPoint
            const spp = toVector3([_x, _y, _z])
            processedItem = {
              ...processedItem,
              perpendicularPoint: pp,
              symmetricalPerpendicularPoint: spp,
            }
          }
        }

        // Procesar zones
        if (item?.zones) {
          const itemCopy = { ...processedItem }
          for (const zoneName in itemCopy.zones) {
            const zone = { ...itemCopy.zones[zoneName] }
            if (zone.position && zone.rotation) {
              const fixedPosition = toVector3([zone.position.x, zone.position.y, zone.position.z])
              const fixedRotation = new Euler(
                zone.rotation._x,
                zone.rotation._y,
                zone.rotation._z,
                zone.rotation._order,
              )
              const fixedScale = toVector3([zone.scale.x, zone.scale.y, zone.scale.z])
              zone.scale = fixedScale
              zone.position = fixedPosition
              zone.rotation = fixedRotation
            }
          }
          processedItem = itemCopy
        }

        // Procesar matrix
        if (item?.matrix) {
          const fixedMatrix = new Matrix4().fromArray(item.matrix.elements)
          processedItem.matrix = fixedMatrix
        }

        // Actualizar childs para linkedItems si existen
        if (item.type === 'linkedItem' && item.childs) {
          const newChilds = new Set<string>()
          item.childs.forEach((childId: string) => {
            const newChildId = idMapping.get(childId)
            if (newChildId) {
              newChilds.add(newChildId)
            }
          })
          processedItem.childs = newChilds
        }

        // Actualizar firstParentID para linkedSon
        if (item.type === 'linkedSon' && item.firstParentID) {
          const newParentId = idMapping.get(item.firstParentID)
          if (newParentId) {
            processedItem.firstParentID = newParentId
          }
        }

        newItems.set(newId, processedItem)
        newIncrement++
      })

      // Precalcular zonas y texturas para los nuevos items cuando sea posible
      // Esto ayuda a que, al cargar proyectos/favoritos, las texturas se apliquen sin esperar a interacciones
      const unidadesBack = (backResponse as any)?.unidades ?? []
      const retryTextureIds: string[] = []
      const texturesStart = performance.now()
      newItems.forEach((item) => {
        if (!item) return
        if (item.type === 'walls' || item.type === 'holes') return

        const key = (item as any).catalogID || (item as any).idUnidades
        if (!key) return

        const sceneEntry = get().sceneGLB.get(key)
        const glb = sceneEntry?.glb as any
        const backGLBInfo = sceneEntry?.backGLBInfo as any

        if (!glb) return

        // No machacar texturas si ya vienen en el proyecto
        const alreadyHasTextures =
          Array.isArray((item as any).textures) && (item as any).textures.length > 0
        if (alreadyHasTextures) return

        // Intentar usar acabados del backGLBInfo; si no, caer a los de backResponse.unidades
        const unidadFromBack = unidadesBack.find(
          (u: any) => u.idUnidades === (item as any).idUnidades,
        )

        const acabadosSource =
          (backGLBInfo && Array.isArray(backGLBInfo.acabados) && backGLBInfo.acabados.length > 0
            ? backGLBInfo.acabados
            : undefined) ||
          (unidadFromBack &&
          Array.isArray(unidadFromBack.acabados) &&
          unidadFromBack.acabados.length > 0
            ? unidadFromBack.acabados
            : undefined)

        if (!acabadosSource) return

        const extraInfo =
          unidadFromBack && Array.isArray(unidadFromBack.acabados)
            ? {
                zonas: unidadFromBack.acabados
                  .filter((a: any) => a && a.idAcabadoUnidad && a.zona)
                  .map((a: any) => ({
                    idAcabadoUnidad: a.idAcabadoUnidad as number,
                    zona: String(a.zona),
                  })),
              }
            : undefined

        const zonesAndTextures =
          getZonesFromGLB(glb.scene?.children, acabadosSource, extraInfo) || {}

        const { zones = {}, textures = [] } = zonesAndTextures as {
          zones: any
          textures: any[]
        }

        ;(item as any).zones = zones
        ;(item as any).textures = textures

        // Si tras el primer intento seguimos sin texturas, marcar para un reintento posterior
        if (!textures || textures.length === 0) {
          const idForRetry = (item as any).id
          if (idForRetry && typeof idForRetry === 'string') {
            retryTextureIds.push(idForRetry)
          }
        }
      })

      // Agregar items a la escena actual
      const addItemsStart = performance.now()
      set((store) => {
        // Agregar items al mapa existente
        newItems.forEach((item, id) => {
          store.items.set(id, item)
          // Agregar a la capa actual
          store.layers[store.currentLayer].items.add(id)
        })

        // Actualizar increment
        store.increment = newIncrement

        // Actualizar sceneBoundingBoxes para los nuevos items
        newItems.forEach((item, id) => {
          if (item.type === 'walls') {
            const wall = item as Walls
            const wallCOBB = getWallCOBB(wall)
            if (wallCOBB) {
              store.sceneBoundingBoxes.set(`COBB_${id}`, {
                type: 'walls',
                boundingBox: wallCOBB,
              })
            }
          } else if (item.type !== 'walls' && item.matrix) {
            // Calcular bounding box para items
            const morphs = (item as Item).morphs || []
            if (morphs.length >= 3) {
              const rotationMatrix4 = new Matrix4().makeRotationFromEuler(
                new Euler(0, item.rotation || 0, 0, 'XYZ'),
              )
              const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
              store.sceneBoundingBoxes.set(id, {
                boundingBox: new OBB(
                  toVector3([-morphs[2].default / 2, morphs[1].default / 2, morphs[0].default / 2]),
                  toVector3([morphs[0].default / 2, morphs[1].default / 2, morphs[2].default / 2]),
                  rotationMatrix,
                ),
                type: item.type,
              })
            }
          }

          // Agregar a collisionableItems si corresponde
          const glbData = store.sceneGLB.get(item.catalogID || item.idUnidades)
          if (glbData?.backGLBInfo) {
            if (glbData.backGLBInfo.colision ?? !glbData.backGLBInfo.universal) {
              store.collisionableItems.add(id)
            }
          }
        })

        // Actualizar precio sumando el precio del proyecto cargado
        store.price = (store.price || 0) + (info.price || 0)

        // Seleccionar todos los items agregados
        const addedItemIds = Array.from(newItems.keys())
        if (addedItemIds.length > 0) {
          // Deseleccionar todo primero
          store.layers[store.currentLayer].selected.clear()

          // Seleccionar todos los items agregados directamente
          addedItemIds.forEach((id) => {
            store.layers[store.currentLayer].selected.add(id)
          })

          // Establecer el primer item como currentObjectID
          store.currentObjectID = addedItemIds[0]
        }

        // Forzar recálculo de texturas para todos los nuevos items cargados
        // (ObjectMeshENC depende de contadorModificaciones para recalcular handleNodeTextures)
        store.contadorModificaciones = (store.contadorModificaciones || 0) + 1
      })

      // Seleccionar los items usando la función select después de que termine el set
      // Esto asegura que la selección múltiple funcione correctamente
      const addedItemIds = Array.from(newItems.keys())
      if (addedItemIds.length > 0) {
        const selectFn = get().select
        const setDeltasFn = get().setDeltas
        const currentItems = get().items

        // Limpiar selección primero
        get().unselectAll()

        // Seleccionar todos los items usando la función select
        addedItemIds.forEach((id, index) => {
          // Para el primer item, no usar Ctrl (limpia la selección anterior)
          // Para los siguientes, usar Ctrl=true para agregar a la selección
          if (index === 0) {
            selectFn(id, false)
          } else {
            selectFn(id, true)
          }
        })

        // Calcular y establecer los deltas inmediatamente después de seleccionar
        // Esto es necesario para que funcione en 3D cuando arrastras los items
        if (addedItemIds.length > 1) {
          const selectedItemsSet = new Set(addedItemIds)
          const deltas = calculateDeltasBetweenSelectedItems(selectedItemsSet, currentItems)
          setDeltasFn(deltas)
        }
      }

      // Forzar invalidación de React Three Fiber
      try {
        const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
        if (r3f?.invalidate) {
          r3f.invalidate()
          requestAnimationFrame(() => {
            r3f.invalidate()
          })
        }
      } catch (r3fError) {
        // Ignorar errores de invalidación
      }

      // Spinner ya encendido con mensaje (MSG_10) desde MenuItem; aquí solo mantener visible.
      const setGlobalSpinner = get().setGlobalSpinner
      if (typeof setGlobalSpinner === 'function') {
        setGlobalSpinner(true)
      }

      // Pequeño "segundo pulso" de contadorModificaciones para forzar
      // un re-render de texturas cuando ya está todo el GLB cargado.
      setTimeout(() => {
        set((s) => {
          s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
        })
        if (typeof setGlobalSpinner === 'function') {
          setGlobalSpinner(false)
        }
      }, 500)

      // Reintento diferido: si algún item no tenía texturas en el primer cálculo,
      // volver a intentar calcularlas unos ms más tarde (cuando todo debería estar cargado)
      if (retryTextureIds.length > 0) {
        setTimeout(() => {
          const state = get()
          const unidadesRetry = (backResponse as any)?.unidades ?? []
          const sceneGLBRetry = state.sceneGLB
          const itemsRetry = state.items

          retryTextureIds.forEach((id) => {
            const item = itemsRetry.get(id as AnyID) as any
            if (!item) return
            if (item.type === 'walls' || item.type === 'holes') return

            const key = item.catalogID || item.idUnidades
            if (!key) return

            const sceneEntry = sceneGLBRetry.get(key)
            const glb = sceneEntry?.glb as any
            const backGLBInfo = sceneEntry?.backGLBInfo as any
            if (!glb) return

            const unidadFromBack = unidadesRetry.find((u: any) => u.idUnidades === item.idUnidades)
            const acabadosSource =
              (backGLBInfo && Array.isArray(backGLBInfo.acabados) && backGLBInfo.acabados.length > 0
                ? backGLBInfo.acabados
                : undefined) ||
              (unidadFromBack &&
              Array.isArray(unidadFromBack.acabados) &&
              unidadFromBack.acabados.length > 0
                ? unidadFromBack.acabados
                : undefined)

            if (!acabadosSource) return

            const extraInfo =
              unidadFromBack && Array.isArray(unidadFromBack.acabados)
                ? {
                    zonas: unidadFromBack.acabados
                      .filter((a: any) => a && a.idAcabadoUnidad && a.zona)
                      .map((a: any) => ({
                        idAcabadoUnidad: a.idAcabadoUnidad as number,
                        zona: String(a.zona),
                      })),
                  }
                : undefined

            const zonesAndTextures =
              getZonesFromGLB(glb.scene?.children, acabadosSource, extraInfo) || {}
            const { zones = {}, textures = [] } = zonesAndTextures as {
              zones: any
              textures: any[]
            }

            if (textures && textures.length > 0) {
              set((s) => {
                const it = s.items.get(id as AnyID) as any
                if (!it) return
                it.zones = zones
                it.textures = textures
                s.items.set(id as AnyID, it)
                s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
              })
            }
          })
        }, 800)
      }

      return true
    } catch (error) {
      console.error('Error agregando items del proyecto a la escena actual:', error)
      return false
    }
  },
  resetStore: () => {
    const drawLight = (id: string, icon: string, catalogID: string) =>
      get().drawLight(id, icon, catalogID, false)
    set((store) => {
      store.items = new Map()
      store.areas = new Map()
      store.layers = baseLayers
      store.price = 0
      store.sceneBoundingBoxes = new Map()
      store.collisionableItems = new Set()
      store.canDraw = true
      store.currentObjectID = ''
      store.increment = 1
      store.canDraw = true
      store.isModalOpen = false
      store.isProjectOpened = false
      store.currentRoof = ''
      store.currentArea = ''
      store.currentLayer = 0
      store.isPathTracing = false
      store.sceneGLB = new Map()
      store.camera2DPosition = baseCamera2DData
      store.camera3DData = baseCamera3DData
      store.clientInfo = { ...emptyClientInfo, idImpuestos: store.impuestos[0].idImpuestos }
      store.savedViews = []
      store.frontalActive = false
      drawLight('lights_baseSceneLight', '', 'LUZGENERAL')
    })
    get().clearAllFreeMeasurements()
  },

  editMorph: (itemID: string, morphTarget: Item['morphs'][number]['name'], value: number) => {
    set((store: Store) => {
      const item = store.items.get(itemID) as Item

      if (!item) return
      const selectedItems = store.layers[store.currentLayer].selected
      const targetIds =
        usesFreeDimensionScaling(item) && isFreeDimensionDimensionMorph(morphTarget)
          ? [itemID]
          : Array.from(selectedItems)
      targetIds.forEach((selectedId: string) => {
        const item = store.items.get(selectedId) as Item | LinkedItem | Hole
        if (!item) return
        const morphs = item.morphs
        if (!morphs) return
        // if (morphTarget === 'Fondo' && item.tipoUnidNorm === 'V') return
        morphs.forEach((el) => {
          if (el.name === morphTarget) {
            if (item.type === 'holes') {
              el._default = value
              el.default = value > el.max ? el.max : value < el.min ? el.min : value
              if (el.name === 'Fondo' && (item as Hole).tipoUnidNorm === 'P') {
                ;(item as any)[MorphDictionary?.[el.name]] = value + (item as any).offset
              } else {
                ;(item as any)[MorphDictionary?.[el.name]] = value
              }
              return
            }
            if (usesFreeDimensionScaling(item) && isFreeDimensionDimensionMorph(morphTarget)) {
              const layoutValue = value
              const storeValue =
                morphTarget === 'Alto' ? getFreeDimensionStoreAlto(layoutValue, item) : layoutValue
              if (morphTarget === 'Ancho' && isFreeDimensionAnchoApiMorphCatalog(item)) {
                const clamped = clampMorphMm(layoutValue, el.min, el.max)
                el._default = clamped
                el.default = clamped
                item.width = clamped
                return
              }
              if (morphTarget === 'Alto' && itemUsesFreeDimensionAltoApiMorph(item)) {
                const clamped = clampMorphMm(layoutValue, el.min, el.max)
                el._default = clamped
                el.default = clamped
                item.height = clamped
                return
              }
              const anchoViaMorph =
                morphTarget === 'Ancho' && itemUsesFreeDimensionAnchoGlbMorph(item)
              const altoViaMorph =
                morphTarget === 'Alto' && itemUsesFreeDimensionAltoGlbMorph(item)
              const fondoViaMorph =
                morphTarget === 'Fondo' && itemUsesFreeDimensionFondoGlbMorph(item)
              if (anchoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el.default = Math.min(layoutValue, morphMax)
                el._default = layoutValue
                item.width = layoutValue
              } else if (altoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el._default = layoutValue
                el.default = Math.min(
                  getFreeDimensionAltoGlbMorphMm(layoutValue, item),
                  morphMax,
                )
                item.height = storeValue
              } else if (fondoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el.default = Math.min(layoutValue, morphMax)
                el._default = layoutValue
                item.thickness = layoutValue
              } else {
                el._default = layoutValue
                if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
                  item[MorphDictionary?.[el.name]] = storeValue + item.offset
                } else {
                  item[MorphDictionary?.[el.name]] = storeValue
                }
              }
              if (morphTarget === 'Ancho') {
                logFreeDimensionAncho('editMorph', item as Item, {
                  sidebarValue: layoutValue,
                  storeValue,
                  anchoViaGlbMorph: anchoViaMorph,
                  morphRefMm: el.default,
                  morphActiveMm: el._default,
                })
              }
              if (morphTarget === 'Fondo') {
                logFreeDimensionFondo('editMorph', item as Item, {
                  sidebarValue: layoutValue,
                  storeThickness: item.thickness,
                  fondoViaGlbMorph: fondoViaMorph,
                  morphRefMm: el.default,
                  morphActiveMm: el._default,
                })
              }
              return
            }
            if (!item?.hasMorphs) {
              el._default = value
              return
            }
            // Solo actualizar default, NO _default (que mantiene el valor por defecto original)
            el.default = value > el.max ? el.max : value < el.min ? el.min : value
            if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
              item[MorphDictionary?.[el.name]] = value + item.offset
            } else if (el.name === 'Regleta' && item.type === 'linkedItem') {
              // Manejo especial para regleta en linkedItems
              ;(item as LinkedItem).regleta = value
            } else {
              // if (el.name === 'Fondo' && item.tipoUnidNorm === 'V') return
              item[MorphDictionary?.[el.name]] = value
            }
          }
        })
      })
    })

    setTimeout(() => {
      const item = get().items.get(itemID)
      if (item && item.type === 'holes') {
        get().updateHole(itemID)
      }
      if (
        item &&
        item.type === 'linkedItem' &&
        (morphTarget === 'Alto' ||
          morphTarget === 'Ancho' ||
          morphTarget === 'Fondo' ||
          morphTarget === 'Regleta') &&
        item.childs &&
        item.childs.size > 0
      ) {
        get().updateLinkedSonPositions(itemID, morphTarget as 'Ancho' | 'Fondo' | 'Alto')
      }
    }, 0)
  },

  editMorphs: (morphTarget: Item['morphs'][number]['name'], value: number) => {
    set((store) => {
      const selectedItems = store.currentLayer[store.currentLayer].selected

      selectedItems.forEach((selectedId: string) => {
        const item = store.items.get(selectedId) as Item | LinkedItem | Hole
        if (!item) return

        const morphs = item.morphs
        if (!morphs) return

        morphs.forEach((el) => {
          if (el.name === morphTarget) {
            if (usesFreeDimensionScaling(item) && isFreeDimensionDimensionMorph(morphTarget)) {
              const layoutValue = value
              const storeValue =
                morphTarget === 'Alto' ? getFreeDimensionStoreAlto(layoutValue, item) : layoutValue
              if (morphTarget === 'Ancho' && isFreeDimensionAnchoApiMorphCatalog(item)) {
                const clamped = clampMorphMm(layoutValue, el.min, el.max)
                el._default = clamped
                el.default = clamped
                item.width = clamped
                return
              }
              if (morphTarget === 'Alto' && itemUsesFreeDimensionAltoApiMorph(item)) {
                const clamped = clampMorphMm(layoutValue, el.min, el.max)
                el._default = clamped
                el.default = clamped
                item.height = clamped
                return
              }
              const anchoViaMorph =
                morphTarget === 'Ancho' && itemUsesFreeDimensionAnchoGlbMorph(item)
              const altoViaMorph =
                morphTarget === 'Alto' && itemUsesFreeDimensionAltoGlbMorph(item)
              const fondoViaMorph =
                morphTarget === 'Fondo' && itemUsesFreeDimensionFondoGlbMorph(item)
              if (anchoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el.default = Math.min(layoutValue, morphMax)
                el._default = layoutValue
                item.width = layoutValue
              } else if (altoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el._default = layoutValue
                el.default = Math.min(
                  getFreeDimensionAltoGlbMorphMm(layoutValue, item),
                  morphMax,
                )
                item.height = storeValue
              } else if (fondoViaMorph) {
                const morphMax =
                  el.max != null && el.min != null && el.max > el.min ? el.max : layoutValue
                el.default = Math.min(layoutValue, morphMax)
                el._default = layoutValue
                item.thickness = layoutValue
              } else {
                el._default = layoutValue
                if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
                  item[MorphDictionary?.[el.name]] = storeValue + item.offset
                } else {
                  item[MorphDictionary?.[el.name]] = storeValue
                }
              }
              return
            }
            if (!item?.hasMorphs) {
              el._default = value
              return
            }
            el.default = value > el.max ? el.max : value < el.min ? el.min : value
            if (el.name === 'Fondo' && item.tipoUnidNorm === 'P') {
              item[MorphDictionary?.[el.name]] = value + item.offset
              return
            }
            item[MorphDictionary?.[el.name]] = value
          }
        })
      })
    })
  },
  deleteSceneObject: (elementID: string) => {
    set((store) => {
      const itemu = store.items.get(elementID)

      // Si es un itemGroup, borrar el grupo y todos sus hijos
      if (itemu?.type === 'itemGroup') {
        const group = itemu as ItemGroup
        const childrenIds = Array.from(group.children) as AnyID[]

        // Borrar cada hijo del grupo
        childrenIds.forEach((childId) => {
          const child = store.items.get(childId)

          if (child && isLinkedItem(child)) {
            removeLinkedHierarchyFromStore(store, childId)
          }

          // Borrar el hijo
          if (child) {
            const childCatalogID = child.catalogID
            const childCodigo = (child as { codigo?: string }).codigo
            store.layers[store.currentLayer].selected.delete(childId)
            store.items.delete(childId)
            store.sceneBoundingBoxes.delete(childId)
            store.collisionableItems.delete(childId)
            purgeSceneGlbForCatalog(store, childCatalogID, childCodigo)
          }
        })

        // Borrar el grupo mismo
        store.layers[store.currentLayer].items.delete(elementID)
        store.layers[store.currentLayer].selected.delete(elementID)
        store.items.delete(elementID)
        store.sceneBoundingBoxes.delete(elementID)
        store.collisionableItems.delete(elementID)
        store.currentObjectID = ''
        return
      }

      if (elementID.startsWith('linkedItem_')) {
        const item = store.items.get(elementID)
        if (!isLinkedItem(item)) return

        const catalogID = item.catalogID
        const codigo = (item as { codigo?: string }).codigo
        removeLinkedHierarchyFromStore(store, elementID)

        store.layers[store.currentLayer].selected.delete(elementID)
        store.layers[store.currentLayer].items.delete(elementID)
        store.items.delete(elementID)
        store.sceneBoundingBoxes.delete(elementID)
        store.collisionableItems.delete(elementID)
        purgeSceneGlbForCatalog(store, catalogID, codigo)
        store.currentObjectID = ''
        return
      }

      if (isWall(itemu)) {
        itemu?.holes.forEach((hole: string) => {
          store.items.delete(hole)
          store.sceneBoundingBoxes.delete(hole)
          store.collisionableItems.delete(hole)
          store.sceneBoundingBoxes.delete(getWallCOBB(hole))
          store.collisionableItems.delete(getWallCOBB(hole))
          store.layers[store.currentLayer].items.delete(hole)
          store.currentObjectID = ''
        })
        itemu.visible = false
        itemu.wallVisible = false
        return
      }

      if (isHole(itemu)) {
        const line = store.items.get(itemu.wall)
        if (isWall(line)) {
          line.holes.delete(elementID)
        }
      }

      if (isItem(itemu) && itemu.snappedWall) {
        const wall = store.items.get(itemu.snappedWall)
        if (isWall(wall)) {
          wall.lowItems.delete(elementID)
          wall.highItems.delete(elementID)
        }
      }

      if (isLinkedItem(itemu)) {
        removeLinkedHierarchyFromStore(store, elementID)
      }

      store.layers[store.currentLayer].selected.delete(elementID)
      store.layers[store.currentLayer].items.delete(elementID)

      const deletedCatalogID = itemu?.catalogID
      const deletedCodigo = itemu ? (itemu as { codigo?: string }).codigo : undefined
      store.items.delete(elementID)
      store.sceneBoundingBoxes.delete(elementID)
      store.sceneBoundingBoxes.delete(getWallCOBB(elementID))
      store.collisionableItems.delete(getWallCOBB(elementID))
      store.collisionableItems.delete(elementID)
      purgeSceneGlbForCatalog(store, deletedCatalogID, deletedCodigo)
      store.currentObjectID = ''

      // console.log(`Deleted item: ${elementID}`)
    })
  },
  testFn: () => {
    set((store: Store) => {
      const object = store.items.get('someID')
      if (!object || object.type !== 'items') return
    })
  },
  openModal: () => {
    set((store: Store) => {
      store.isModalOpen = true
    })
  },
  closeModal: () => {
    set((store: Store) => {
      store.isModalOpen = false
    })
  },
  openProyect: () => {
    set((store: Store) => {
      store.isProjectOpened = true
    })
  },
  setImpuestos: (impuestos: Store['impuestos']) => {
    set((store) => {
      if (!impuestos) return
      store.impuestos = impuestos
      store.clientInfo.idImpuestos = impuestos.find((el) => el.porDefecto)?.idImpuestos ?? 0
    })
  },
  editCameraBoundingBox: (position: Vector3, rotation: Euler) => {
    set((store) => {
      const camObb = store.cameraOBB
      if (!camObb) return
      let dist = 0
      // Verificar si la posición está dentro del área formada por las paredes
      const walls = Array.from(store.items.values()).filter((item) => item.type === 'walls')
      const wallPoints = walls.flatMap((wall) => [wall.start, wall.end])
      // Algoritmo simple: proyectar en XZ y usar ray-casting para saber si está dentro del polígono
      const isPointInPolygon = (point: { x: number; z: number }, polygon: number[][]) => {
        const x = point.x,
          z = point.z
        let inside = false
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i][0],
            zi = polygon[i][1]
          const xj = polygon[j][0],
            zj = polygon[j][1]
          const intersect = zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi || 1) + xi
          if (intersect) inside = !inside
        }
        return inside
      }
      const polygon = wallPoints.map(([x, z]) => [x, z])
      if (!isPointInPolygon(position, polygon)) {
        // Calcular el centro del polígono en XZ
        let centerX = 0,
          centerZ = 0
        if (polygon.length > 0) {
          for (const [x, z] of polygon) {
            centerX += x
            centerZ += z
          }
          centerX /= polygon.length
          centerZ /= polygon.length
        }
        // La posición de la cámara es position.x, position.z
        // Calcular la distancia en XZ
        const dx = position.x - centerX
        const dz = position.z - centerZ
        dist = Math.sqrt(dx * dx + dz * dz)
      }
      if (typeof dist != 'number') {
        dist = Number(dist)
      }
      const m4 = new Matrix4().makeRotationFromEuler(rotation)
      const m3 = new Matrix3().setFromMatrix4(m4)
      camObb.set(position, toVector3([400, 50, dist]), m3)
    })
  },
  projectItemOnWall: (
    itemID: AnyID,
    wallID: AnyID,
    matrix: Matrix4,
    skipContiguityCheck = false,
    preserveY = false,
  ) => {
    set((store) => {
      const items = store.items
      const item = store.items.get(itemID) as Item | LinkedItem
      // Buscar el morph de ancho de manera más robusta
      const itemWidthMorph = item.morphs?.find(
        (morph) => morph.name === 'Ancho' || morph.name === 'width',
      )
      const itemWidth = item.long?.toLowerCase().includes('encimera b3200')
        ? (itemWidthMorph?._default ?? item.width ?? 0)
        : item.long !== 'Frontal' || item.long?.toLowerCase().includes('zocalo')
          ? (itemWidthMorph?.default ?? item.width ?? 0)
          : (itemWidthMorph?._default ?? item.width ?? 0)
      if (item.catalogID && item.catalogID.toString().startsWith('CORTEKIT')) {
        return // Saltar procesamiento para kits de corte
      }
      const wall = items.get(wallID) as Walls
      const { rotation, width } = wall
      const itemPosition = toVector3()
      const quaternion = toQuaternion()
      matrix.decompose(itemPosition, quaternion, toVector3())
      const end = toVector3(wall.end)
      const start = toVector3(wall.start)
      const wallDirection = end.clone().sub(start)
      const itemToStart = itemPosition.clone().sub(start)
      const projectedVector = itemToStart.projectOnVector(wallDirection.clone().normalize())
      // Calcular el centro del item
      const itemCenter2 = new Vector3(item.width / 2, item.height / 2, item.thickness / 2)
      // Aplicar la rotación al centro del item
      itemCenter2.applyQuaternion(quaternion).add(itemPosition)
      if (Math.abs(projectedVector.x) === 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) === 0) {
        projectedVector.z = 0
      }
      if (Math.abs(projectedVector.x) > width - itemWidth) {
        if (wallDirection.x < 0) {
          projectedVector.x = itemWidth - width
        } else {
          projectedVector.x = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.x) < 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) > width - itemWidth) {
        if (wallDirection.z < 0) {
          projectedVector.z = itemWidth - width
        } else {
          projectedVector.z = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.z) < 0) {
        projectedVector.z = -itemWidth
      }
      let length = projectedVector.length()
      const wallLength = wallDirection.length()
      let alongWall = projectedVector.length()
      // Guardar el valor original antes del ajuste para detectar cuando no cabe
      const originalAlongWall = alongWall
      if (projectedVector.dot(wallDirection.normalize()) < 0) {
        alongWall = 0
      } else if (alongWall > wallLength) {
        alongWall = wallLength - itemWidth
      }
      // Verificar si hay espacio disponible a la derecha (desde la posición proyectada hasta el final)
      const wallDirNormalized = wallDirection.clone().normalize()
      const spaceToEnd = wallLength - alongWall

      // Verificar si el item cabe al final de la pared (usar el valor original antes del ajuste)
      const itemFitsAtEnd = originalAlongWall + itemWidth <= wallLength

      // Función auxiliar para verificar si hay otros muebles ocupando espacio
      const checkSpaceAvailable = (positionAlongWall: number): boolean => {
        // Asegurar que la posición esté dentro de los límites de la pared
        if (positionAlongWall < 0) return false
        if (positionAlongWall + itemWidth > wallLength) return false
        const checkStart = positionAlongWall
        const checkEnd = positionAlongWall + itemWidth

        // Obtener todos los muebles en la pared (excepto el actual)
        const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
        allItemsOnWall.delete(itemID)

        // Filtrar por tipo: MAL solo compite con MAL, MBJ/COL compiten entre sí
        const itemTipo = item.tipoNormalizado

        for (const otherItemID of allItemsOnWall) {
          const otherItem = items.get(otherItemID) as Item | LinkedItem
          if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

          // Filtrar por tipo: solo considerar items del mismo tipo para verificación de espacio
          // MAL compite con MAL y COL (porque MAL puede estar al lado de COL)
          // MBJ/COL compiten entre sí
          if (
            itemTipo === 'MAL' &&
            otherItem.tipoNormalizado !== 'MAL' &&
            otherItem.tipoNormalizado !== 'COL'
          ) {
            continue // MAL compite con MAL y COL
          }
          if (
            (itemTipo === 'MBJ' || itemTipo === 'COL') &&
            otherItem.tipoNormalizado !== 'MBJ' &&
            otherItem.tipoNormalizado !== 'COL'
          ) {
            continue // MBJ/COL solo compiten con MBJ/COL
          }

          const otherItemWidthMorph = otherItem.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
            ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
            : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
              ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
              : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
          // Usar la matriz del mueble para mayor precisión (igual que en la búsqueda a la izquierda)
          const otherItemPos = new Vector3()
          const otherItemQuat = new Quaternion()
          otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())
          const otherItemToStart = otherItemPos.clone().sub(start)
          const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))
          const otherItemStart = otherAlongWall
          const otherItemEnd = otherItemStart + otherItemWidth
          // Verificar si hay solapamiento (los rangos se solapan)
          if (checkStart < otherItemEnd && checkEnd > otherItemStart) {
            return false // Hay solapamiento
          }
        }
        return true // No hay solapamiento
      }
      // **LÓGICA DE GRUPO CONTIGUO: Detectar grupo contiguo del mueble seleccionado**
      // Solo ejecutar si hay items seleccionados Y el item actual NO está seleccionado (para evitar interferir con el arrastre)
      // Y solo si NO se está saltando la verificación de contigüidad (arrastre manual)
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      let contiguousGroup: { firstItem: AnyID; lastItem: AnyID; items: AnyID[] } | null = null
      let selectedItemID: AnyID | null = null

      // Si skipContiguityCheck es true, saltar toda la lógica de grupos contiguos (arrastre manual)
      if (!skipContiguityCheck) {
        // Buscar grupo contiguo:
        // 1. Si hay otros items seleccionados, usar esos para encontrar el grupo
        // 2. Si solo el item actual está seleccionado, buscar el grupo más cercano en la misma pared
        const otherSelectedItems = Array.from(selectedItems).filter((id) => id !== itemID)

        if (otherSelectedItems.length > 0) {
          // Hay otros items seleccionados, buscar el grupo contiguo basándose en ellos
          selectedItemID = otherSelectedItems[0]
        } else {
          // No hay otros items seleccionados, buscar el grupo más cercano en la misma pared
          const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
          allItemsOnWall.delete(itemID)

          // Obtener todos los grupos contiguos
          const contiguousResult = get().getContiguousItems()

          // Encontrar el grupo más cercano al item actual en la misma pared
          let closestGroup: (Item | LinkedItem)[] | null = null
          let minDistance = Infinity

          for (const group of contiguousResult.groups) {
            // Verificar que el grupo esté en la misma pared y que el item actual NO esté en el grupo
            const groupItemIDs = group.map((i) => i.id)

            // Si el item está en el grupo, simplemente excluirlo del grupo y evaluar el resto
            // Esto permite que el item se coloque correctamente dentro del grupo
            let groupToEvaluate = group
            if (groupItemIDs.includes(itemID)) {
              // Crear un grupo temporal sin el item actual para evaluarlo
              groupToEvaluate = group.filter((i) => i.id !== itemID)
              if (groupToEvaluate.length === 0) {
                continue
              }
            }

            const firstGroupItem = items.get(groupToEvaluate[0].id) as Item | LinkedItem
            if (!firstGroupItem) {
              continue
            }
            if (firstGroupItem.snappedWall !== wallID) {
              continue
            }

            // Verificar que el tipoNormalizado del item actual coincida con el del grupo
            // MAL solo se agrupa con MAL, MBJ/COL solo con MBJ/COL
            // PERO: Si un MAL se dibuja después de un MBJ/COL, puede usar el grupo del MBJ/COL
            // para determinar la colocación (derecha/izquierda), aunque no se agrupe con ellos
            const itemTipo = item.tipoNormalizado
            const groupTipo = firstGroupItem.tipoNormalizado
            // Permitir que MAL use el grupo de MBJ/COL para colocación (pero no para agrupación)
            const isMALUsingMBJGroup =
              itemTipo === 'MAL' && (groupTipo === 'MBJ' || groupTipo === 'COL')
            if (itemTipo !== groupTipo && !isMALUsingMBJGroup) {
              // Si el item es MAL y el grupo es MBJ/COL o viceversa, no agrupar
              // (excepto si es MAL usando grupo MBJ/COL para colocación)
              if (
                (itemTipo === 'MAL' && (groupTipo === 'MBJ' || groupTipo === 'COL')) ||
                ((itemTipo === 'MBJ' || itemTipo === 'COL') && groupTipo === 'MAL')
              ) {
                continue
              }
            }

            // Calcular la distancia del item actual al grupo (usar el último item del grupo)
            const lastGroupItem = items.get(groupToEvaluate[groupToEvaluate.length - 1].id) as
              | Item
              | LinkedItem
            if (!lastGroupItem) continue

            const lastGroupItemPos = new Vector3()
            const lastGroupItemQuat = new Quaternion()
            lastGroupItem.matrix.decompose(lastGroupItemPos, lastGroupItemQuat, new Vector3())

            // Calcular distancia proyectada sobre la pared usando alongWall que ya está calculado
            // alongWall es la posición proyectada del item actual sobre la pared
            const lastGroupItemToStart = lastGroupItemPos.clone().sub(start)
            const lastGroupItemAlongWall = Math.max(0, lastGroupItemToStart.dot(wallDirNormalized))

            // Calcular dónde termina el último item del grupo usando la misma lógica que en la colocación
            const lastGroupItemWidthMorph = lastGroupItem.morphs?.find(
              (morph) => morph.name === 'Ancho' || morph.name === 'width',
            )
            const lastGroupItemWidth = lastGroupItem.long?.toLowerCase().includes('encimera b3200')
              ? (lastGroupItemWidthMorph?._default ?? lastGroupItem.width ?? 0)
              : lastGroupItem.long !== 'Frontal' ||
                  lastGroupItem.long?.toLowerCase().includes('zocalo')
                ? (lastGroupItemWidthMorph?.default ?? lastGroupItem.width ?? 0)
                : (lastGroupItemWidthMorph?._default ?? lastGroupItem.width ?? 0)

            const lastGroupItemAngle = radians(
              get().getInitialAngleDegrees(groupToEvaluate[groupToEvaluate.length - 1].id),
            )
            const lastGroupItemCos = Math.cos(lastGroupItemAngle)
            const lastGroupItemSin = Math.sin(lastGroupItemAngle)

            const lastGroupItemP = { x: lastGroupItemPos.x, z: lastGroupItemPos.z }
            const lastGroupItemVertices = [
              { x: 0, z: 0 },
              { x: lastGroupItemWidth, z: 0 },
              {
                x: lastGroupItemWidth,
                z: (lastGroupItem.thickness || 0) + ((lastGroupItem as LinkedItem).regleta || 0),
              },
              {
                x: 0,
                z: (lastGroupItem.thickness || 0) + ((lastGroupItem as LinkedItem).regleta || 0),
              },
            ].map((vertex) => ({
              x: lastGroupItemP.x + vertex.x * lastGroupItemCos - vertex.z * lastGroupItemSin,
              z: lastGroupItemP.z + vertex.x * lastGroupItemSin + vertex.z * lastGroupItemCos,
            }))

            let lastGroupItemMaxAlongWall = 0
            for (const vertex of lastGroupItemVertices) {
              const vertexToStart = toVector3([vertex.x, 0, vertex.z]).sub(start)
              const vertexAlongWall = Math.max(0, vertexToStart.dot(wallDirNormalized))
              lastGroupItemMaxAlongWall = Math.max(lastGroupItemMaxAlongWall, vertexAlongWall)
            }

            // Calcular distancia desde la posición proyectada del item actual hasta donde termina el último item del grupo
            // Si el item está a la derecha del grupo, la distancia es positiva
            // Si está a la izquierda, la distancia es negativa
            const distance = alongWall - lastGroupItemMaxAlongWall

            // Preferir grupos que están a la izquierda del item (distancia positiva pequeña)
            // o grupos muy cercanos (distancia pequeña en valor absoluto)
            const absDistance = Math.abs(distance)

            // Si el item está cerca del grupo (menos de 500mm) o está a la derecha del grupo
            if (absDistance < minDistance || (distance > 0 && distance < 1000)) {
              minDistance = absDistance
              closestGroup = groupToEvaluate
              selectedItemID = groupToEvaluate[0].id
            }
          }

          // Aumentar el umbral para asegurar que siempre encuentre grupos cercanos
          // El umbral de 5000mm (5 metros) es razonable para una pared
          if (!(closestGroup && minDistance < 5000)) {
            selectedItemID = null
          }
        }
      }

      if (selectedItemID && !skipContiguityCheck) {
        const selectedItem = items.get(selectedItemID) as Item | LinkedItem
        if (selectedItem && selectedItem.snappedWall === wallID) {
          // Verificar que el tipoNormalizado del item actual coincida con el del selectedItem
          // MAL solo se agrupa con MAL, MBJ/COL solo con MBJ/COL
          // PERO: Si un MAL se dibuja después de un MBJ/COL, puede usar el grupo del MBJ/COL
          // para determinar la colocación (derecha/izquierda), aunque no se agrupe con ellos
          const itemTipo = item.tipoNormalizado
          const selectedTipo = selectedItem.tipoNormalizado
          const isMALUsingMBJGroup =
            itemTipo === 'MAL' && (selectedTipo === 'MBJ' || selectedTipo === 'COL')
          // Solo invalidar si NO es el caso especial de MAL usando grupo MBJ/COL para colocación
          if (
            !isMALUsingMBJGroup &&
            ((itemTipo === 'MAL' && (selectedTipo === 'MBJ' || selectedTipo === 'COL')) ||
              ((itemTipo === 'MBJ' || itemTipo === 'COL') && selectedTipo === 'MAL'))
          ) {
            // No agrupar si son de diferentes alturas (excepto MAL usando grupo MBJ/COL)
            selectedItemID = null
          }
        }
        if (selectedItemID && selectedItem && selectedItem.snappedWall === wallID) {
          // Obtener el grupo contiguo usando getContiguousItems
          const contiguousResult = get().getContiguousItems()
          const group = contiguousResult.groups.find((g) => g.some((i) => i.id === selectedItemID))
          if (group && group.length > 0) {
            // IMPORTANTE: Filtrar el item actual del grupo antes de calcular posiciones
            // Esto evita que el item se use como referencia para sí mismo
            const filteredGroup = group.filter((i) => i.id !== itemID)

            if (filteredGroup.length > 0) {
              // El grupo ya está ordenado, el primero es [0] y el último es [length-1]
              const firstItemID = filteredGroup[0].id
              const lastItemID = filteredGroup[filteredGroup.length - 1].id
              const groupItemIDs = filteredGroup.map((i) => i.id)
              contiguousGroup = {
                firstItem: firstItemID,
                lastItem: lastItemID,
                items: groupItemIDs,
              }
            }
          }
        }
      }
      // **LÓGICA DE COLOCACIÓN BASADA EN GRUPO CONTIGUO**
      let placedInContiguousGroup = false

      if (contiguousGroup) {
        const lastItem = items.get(contiguousGroup.lastItem) as Item | LinkedItem
        const firstItem = items.get(contiguousGroup.firstItem) as Item | LinkedItem
        if (
          lastItem &&
          firstItem &&
          lastItem.snappedWall === wallID &&
          firstItem.snappedWall === wallID
        ) {
          // MAL debe comportarse igual que MBJ: siempre buscar COL como referencia si existe
          // Esto aplica tanto cuando MAL está con MBJ/COL como cuando está directamente con COL o con otro MAL
          const itemTipo = item.tipoNormalizado
          const groupTipo = lastItem.tipoNormalizado
          const isMALUsingMBJGroup =
            itemTipo === 'MAL' && (groupTipo === 'MBJ' || groupTipo === 'COL')

          let referenceItem: Item | LinkedItem = lastItem
          let referenceItemID: AnyID = contiguousGroup.lastItem

          // Para MAL: siempre buscar COL como referencia derecha si existe (igual que MBJ)
          // Esto asegura que MAL se comporte igual que MBJ con COL y con las paredes
          if (itemTipo === 'MAL') {
            // Buscar un COL en el grupo para usarlo como referencia
            for (const groupItemID of contiguousGroup.items) {
              const groupItem = items.get(groupItemID) as Item | LinkedItem
              if (groupItem && isLinkedItem(groupItem) && groupItem.tipoNormalizado === 'COL') {
                referenceItem = groupItem
                referenceItemID = groupItemID
                break
              }
            }
          }

          // Calcular dónde termina el mueble de referencia (COL si existe, sino último del grupo)
          const referenceItemPos = new Vector3()
          const referenceItemQuat = new Quaternion()
          referenceItem.matrix.decompose(referenceItemPos, referenceItemQuat, new Vector3())
          const referenceItemWidthMorph = referenceItem.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const referenceItemWidth = referenceItem.long?.toLowerCase().includes('encimera b3200')
            ? (referenceItemWidthMorph?._default ?? referenceItem.width ?? 0)
            : referenceItem.long !== 'Frontal' ||
                referenceItem.long?.toLowerCase().includes('zocalo')
              ? (referenceItemWidthMorph?.default ?? referenceItem.width ?? 0)
              : (referenceItemWidthMorph?._default ?? referenceItem.width ?? 0)
          // Calcular los vértices del mueble de referencia para obtener dónde termina realmente
          const referenceItemAngle = radians(get().getInitialAngleDegrees(referenceItemID))
          const referenceItemCos = Math.cos(referenceItemAngle)
          const referenceItemSin = Math.sin(referenceItemAngle)
          const referenceItemP = { x: referenceItemPos.x, z: referenceItemPos.z }
          const referenceItemVertices = [
            { x: 0, z: 0 },
            { x: referenceItemWidth, z: 0 },
            {
              x: referenceItemWidth,
              z: (referenceItem.thickness || 0) + ((referenceItem as LinkedItem).regleta || 0),
            },
            {
              x: 0,
              z: (referenceItem.thickness || 0) + ((referenceItem as LinkedItem).regleta || 0),
            },
          ].map((vertex) => ({
            x: referenceItemP.x + vertex.x * referenceItemCos - vertex.z * referenceItemSin,
            z: referenceItemP.z + vertex.x * referenceItemSin + vertex.z * referenceItemCos,
          }))
          let referenceItemMaxAlongWall = 0
          for (const vertex of referenceItemVertices) {
            const vertexToStart = toVector3([vertex.x, 0, vertex.z]).sub(start)
            const vertexAlongWall = Math.max(0, vertexToStart.dot(wallDirNormalized))
            referenceItemMaxAlongWall = Math.max(referenceItemMaxAlongWall, vertexAlongWall)
          }
          const positionAfterReference = referenceItemMaxAlongWall
          // Función especial para verificar espacio excluyendo el mueble de referencia del grupo
          const checkSpaceAvailableExcludingReference = (positionAlongWall: number): boolean => {
            if (positionAlongWall < 0) return false
            if (positionAlongWall + itemWidth > wallLength) return false
            const checkStart = positionAlongWall
            const checkEnd = positionAlongWall + itemWidth

            // Obtener todos los muebles en la pared (excepto el actual Y el mueble de referencia)
            const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
            allItemsOnWall.delete(itemID)
            allItemsOnWall.delete(referenceItemID) // Excluir el mueble de referencia del grupo

            // Si un MAL está usando el grupo de MBJ/COL:
            // - Ignorar MBJ (porque el MAL va encima, no hay colisión horizontal)
            // - SÍ verificar COL (porque están al mismo nivel horizontal, hay colisión)
            // - SÍ verificar otros MAL (porque están al mismo nivel horizontal)
            const itemTipo = item.tipoNormalizado
            const groupTipo = referenceItem.tipoNormalizado
            const isMALUsingMBJGroup =
              itemTipo === 'MAL' && (groupTipo === 'MBJ' || groupTipo === 'COL')

            for (const otherItemID of allItemsOnWall) {
              const otherItem = items.get(otherItemID) as Item | LinkedItem
              if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

              // Si es MAL usando grupo MBJ/COL, solo ignorar MBJ (va encima), pero SÍ verificar COL y MAL
              if (isMALUsingMBJGroup && otherItem.tipoNormalizado === 'MBJ') {
                continue // Ignorar MBJ porque el MAL va encima de ellos (no hay colisión horizontal)
              }

              const otherItemWidthMorph = otherItem.morphs?.find(
                (morph) => morph.name === 'Ancho' || morph.name === 'width',
              )
              const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
                ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
                : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
                  ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
                  : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
              const otherItemPos = new Vector3()
              const otherItemQuat = new Quaternion()
              otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())
              const otherItemToStart = otherItemPos.clone().sub(start)
              const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))
              const otherItemStart = otherAlongWall
              const otherItemEnd = otherItemStart + otherItemWidth
              // Verificar si hay solapamiento (los rangos se solapan)
              if (checkStart < otherItemEnd && checkEnd > otherItemStart) {
                return false // Hay solapamiento
              }
            }
            return true // No hay solapamiento
          }
          // 1. Intentar primero a la derecha del mueble de referencia (COL si existe, sino último del grupo)
          // IMPORTANTE: positionAfterReference es donde TERMINA el mueble de referencia, que es donde debe EMPEZAR el nuevo mueble
          // El punto de pivote del nuevo mueble está en su esquina inferior izquierda, así que colocarlo en positionAfterReference es correcto
          const hasSpaceRight = positionAfterReference + itemWidth <= wallLength
          const spaceAvailableRight = checkSpaceAvailableExcludingReference(positionAfterReference)

          if (hasSpaceRight && spaceAvailableRight) {
            // Usar la posición exacta donde termina el mueble de referencia del grupo
            // Esto asegura que el nuevo mueble quede pegado exactamente
            // IMPORTANTE: Usar exactamente referenceItemMaxAlongWall sin redondeos ni ajustes
            alongWall = referenceItemMaxAlongWall
            placedInContiguousGroup = true
          } else {
            // 2. Si no cabe a la derecha, intentar a la izquierda del mueble de referencia
            // Si hay COL, usar COL como referencia; sino usar el primer mueble del grupo
            // MAL debe comportarse igual que MBJ: buscar COL como referencia si existe
            let leftReferenceItem: Item | LinkedItem = firstItem
            let leftReferenceItemID: AnyID = contiguousGroup.firstItem

            // Para MAL: siempre buscar COL como referencia izquierda si existe (igual que MBJ)
            // Esto aplica tanto cuando MAL está con MBJ/COL como cuando está directamente con COL
            if (itemTipo === 'MAL') {
              // Buscar un COL en el grupo para usarlo como referencia izquierda
              for (const groupItemID of contiguousGroup.items) {
                const groupItem = items.get(groupItemID) as Item | LinkedItem
                if (groupItem && isLinkedItem(groupItem) && groupItem.tipoNormalizado === 'COL') {
                  leftReferenceItem = groupItem
                  leftReferenceItemID = groupItemID
                  break
                }
              }
            }

            const leftReferenceItemPos = new Vector3()
            const leftReferenceItemQuat = new Quaternion()
            leftReferenceItem.matrix.decompose(
              leftReferenceItemPos,
              leftReferenceItemQuat,
              new Vector3(),
            )

            // IMPORTANTE: Calcular los vértices del item de referencia izquierdo para obtener dónde REALMENTE empieza
            // Similar a como se hace para el item de referencia derecho (referenceItemMaxAlongWall)
            const leftReferenceItemWidthMorph = leftReferenceItem.morphs?.find(
              (morph) => morph.name === 'Ancho' || morph.name === 'width',
            )
            const leftReferenceItemWidth = leftReferenceItem.long
              ?.toLowerCase()
              .includes('encimera b3200')
              ? (leftReferenceItemWidthMorph?._default ?? leftReferenceItem.width ?? 0)
              : leftReferenceItem.long !== 'Frontal' ||
                  leftReferenceItem.long?.toLowerCase().includes('zocalo')
                ? (leftReferenceItemWidthMorph?.default ?? leftReferenceItem.width ?? 0)
                : (leftReferenceItemWidthMorph?._default ?? leftReferenceItem.width ?? 0)

            const leftReferenceItemAngle = radians(
              get().getInitialAngleDegrees(leftReferenceItemID),
            )
            const leftReferenceItemCos = Math.cos(leftReferenceItemAngle)
            const leftReferenceItemSin = Math.sin(leftReferenceItemAngle)
            const leftReferenceItemP = { x: leftReferenceItemPos.x, z: leftReferenceItemPos.z }
            const leftReferenceItemVertices = [
              { x: 0, z: 0 },
              { x: leftReferenceItemWidth, z: 0 },
              {
                x: leftReferenceItemWidth,
                z:
                  (leftReferenceItem.thickness || 0) +
                  ((leftReferenceItem as LinkedItem).regleta || 0),
              },
              {
                x: 0,
                z:
                  (leftReferenceItem.thickness || 0) +
                  ((leftReferenceItem as LinkedItem).regleta || 0),
              },
            ].map((vertex) => ({
              x:
                leftReferenceItemP.x +
                vertex.x * leftReferenceItemCos -
                vertex.z * leftReferenceItemSin,
              z:
                leftReferenceItemP.z +
                vertex.x * leftReferenceItemSin +
                vertex.z * leftReferenceItemCos,
            }))

            // Obtener la posición MÍNIMA (donde REALMENTE empieza el item de referencia considerando todos sus vértices)
            let leftReferenceItemMinAlongWall = Infinity
            for (const vertex of leftReferenceItemVertices) {
              const vertexToStart = toVector3([vertex.x, 0, vertex.z]).sub(start)
              const vertexAlongWall = Math.max(0, vertexToStart.dot(wallDirNormalized))
              leftReferenceItemMinAlongWall = Math.min(
                leftReferenceItemMinAlongWall,
                vertexAlongWall,
              )
            }

            const positionBeforeReference = leftReferenceItemMinAlongWall - itemWidth

            // Función especial para verificar espacio excluyendo el mueble de referencia izquierdo
            const checkSpaceAvailableExcludingLeftReference = (
              positionAlongWall: number,
            ): boolean => {
              if (positionAlongWall < 0) return false
              if (positionAlongWall + itemWidth > wallLength) return false
              const checkStart = positionAlongWall
              const checkEnd = positionAlongWall + itemWidth

              const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
              allItemsOnWall.delete(itemID)
              allItemsOnWall.delete(leftReferenceItemID) // Excluir el mueble de referencia izquierdo

              // Si un MAL está usando el grupo de MBJ/COL:
              // - Ignorar MBJ (porque el MAL va encima, no hay colisión horizontal)
              // - SÍ verificar COL (porque están al mismo nivel horizontal, hay colisión)
              // - SÍ verificar otros MAL (porque están al mismo nivel horizontal)
              const itemTipo = item.tipoNormalizado
              const groupTipo = leftReferenceItem.tipoNormalizado
              const isMALUsingMBJGroup =
                itemTipo === 'MAL' && (groupTipo === 'MBJ' || groupTipo === 'COL')

              for (const otherItemID of allItemsOnWall) {
                const otherItem = items.get(otherItemID) as Item | LinkedItem
                if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID)
                  continue

                // Si es MAL usando grupo MBJ/COL, solo ignorar MBJ (va encima), pero SÍ verificar COL y MAL
                if (isMALUsingMBJGroup && otherItem.tipoNormalizado === 'MBJ') {
                  continue // Ignorar MBJ porque el MAL va encima de ellos (no hay colisión horizontal)
                }

                const otherItemWidthMorph = otherItem.morphs?.find(
                  (morph) => morph.name === 'Ancho' || morph.name === 'width',
                )
                const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
                  ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
                  : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
                    ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
                    : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
                const otherItemPos = new Vector3()
                const otherItemQuat = new Quaternion()
                otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())
                const otherItemToStart = otherItemPos.clone().sub(start)
                const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))
                const otherItemStart = otherAlongWall
                const otherItemEnd = otherItemStart + otherItemWidth
                if (checkStart < otherItemEnd && checkEnd > otherItemStart) {
                  return false
                }
              }
              return true
            }
            const hasSpaceLeft = positionBeforeReference >= 0
            const spaceAvailableLeft =
              checkSpaceAvailableExcludingLeftReference(positionBeforeReference)

            if (hasSpaceLeft && spaceAvailableLeft) {
              alongWall = positionBeforeReference
              placedInContiguousGroup = true
            } else {
              // No cabe ni a la derecha ni a la izquierda del grupo contiguo
              // Permitir que entre en la lógica de wrapping general para buscar espacio en otros lugares
              placedInContiguousGroup = false
              // Restaurar alongWall al valor original ajustado para que el bloque de wrapping use el valor correcto
              // Esto asegura que se verifique correctamente si necesita wrapping
              if (projectedVector.dot(wallDirection.normalize()) < 0) {
                alongWall = 0
              } else if (originalAlongWall > wallLength) {
                alongWall = wallLength - itemWidth
              } else {
                alongWall = originalAlongWall
              }
            }
          }
        }
      }

      // **VERIFICACIÓN POST-GRUPO CONTIGUO**: Si se colocó en un grupo contiguo, verificar si realmente cabe
      // Para MAL, MBJ y COL: Si hay espacio disponible más adelante, resetear placedInContiguousGroup para usar ese espacio
      // Esto asegura que todos se comporten igual: usar espacio disponible cuando existe, no forzar grupo contiguo
      if (
        placedInContiguousGroup &&
        (item.tipoNormalizado === 'MAL' ||
          item.tipoNormalizado === 'MBJ' ||
          item.tipoNormalizado === 'COL')
      ) {
        // Verificar si realmente hay espacio disponible en la posición calculada del grupo contiguo
        const actuallyHasSpace =
          checkSpaceAvailable(alongWall) && alongWall + itemWidth <= wallLength

        // Verificar si hay espacio disponible desde la posición original hasta el final (como MAL)
        let hasBetterSpaceAhead = false
        if (originalAlongWall + itemWidth <= wallLength) {
          for (
            let checkPos = originalAlongWall;
            checkPos <= wallLength - itemWidth;
            checkPos += 10
          ) {
            if (checkSpaceAvailable(checkPos)) {
              hasBetterSpaceAhead = true
              break
            }
          }
        }

        // Si no cabe realmente O hay mejor espacio disponible más adelante, permitir que use ese espacio
        if (!actuallyHasSpace || hasBetterSpaceAhead) {
          placedInContiguousGroup = false
          // Restaurar alongWall al valor original para que use el espacio disponible
          if (projectedVector.dot(wallDirection.normalize()) < 0) {
            alongWall = 0
          } else if (originalAlongWall > wallLength) {
            alongWall = wallLength - itemWidth
          } else {
            alongWall = originalAlongWall
          }
        }
      }

      // Recalcular spaceToEnd después de la lógica de grupo contiguo (por si alongWall cambió)
      const spaceToEndAfterGroup = wallLength - alongWall

      // **NUEVA LÓGICA: FORZAR CONTIGÜIDAD CUANDO SE COLOCA A LA IZQUIERDA**
      const forceContiguity = (targetPosition: number): number => {
        // Buscar el mueble más cercano a la derecha de esta posición
        let closestRightItem: { id: AnyID; startPos: number; width: number } | null = null
        let minDistance = Infinity
        const allItemsOnWallForContiguity = new Set([...wall.highItems, ...wall.lowItems])
        allItemsOnWallForContiguity.delete(itemID)
        for (const otherItemID of allItemsOnWallForContiguity) {
          const otherItem = items.get(otherItemID) as Item | LinkedItem
          if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

          const otherItemWidthMorph = otherItem.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
            ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
            : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
              ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
              : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
          const otherItemPos = new Vector3()
          const otherItemQuat = new Quaternion()
          otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())
          const otherItemToStart = otherItemPos.clone().sub(start)
          const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))
          // Si está a la derecha de nuestra posición
          if (otherAlongWall > targetPosition) {
            const distance = otherAlongWall - targetPosition
            if (distance < minDistance) {
              minDistance = distance
              closestRightItem = {
                id: otherItemID,
                startPos: otherAlongWall,
                width: otherItemWidth,
              }
            }
          }
        }
        // Si hay un mueble a la derecha, pegarnos a él
        if (closestRightItem && minDistance > 0) {
          // Calcular posición pegada al mueble de la derecha
          const newPosition = closestRightItem.startPos - itemWidth
          // Asegurar que no solape con muebles a la izquierda
          let leftConflict = false
          for (const otherItemID of allItemsOnWallForContiguity) {
            const otherItem = items.get(otherItemID) as Item | LinkedItem
            if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

            const otherItemWidthMorph = otherItem.morphs?.find(
              (morph) => morph.name === 'Ancho' || morph.name === 'width',
            )
            const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
              ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
              : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
                ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
                : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
            const otherItemPos = new Vector3()
            const otherItemQuat = new Quaternion()
            otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())
            const otherItemToStart = otherItemPos.clone().sub(start)
            const otherAlongWall = Math.max(0, otherItemToStart.dot(wallDirNormalized))
            const otherItemEnd = otherAlongWall + otherItemWidth
            // Verificar solapamiento
            if (newPosition < otherItemEnd && newPosition + itemWidth > otherAlongWall) {
              leftConflict = true
              break
            }
          }
          if (!leftConflict && newPosition >= 0) {
            return newPosition
          }
        }
        return targetPosition
      }
      // Si se colocó en el grupo contiguo, saltar la lógica estándar
      // Si no hay espacio suficiente a la derecha Y no se colocó en grupo contiguo, intentar colocar desde el final hacia atrás (izquierda)
      // También entrar si el item no cabe al final de la pared (para hacer wrapping)
      // Para MAL y MBJ: usar la misma lógica - solo entrar en wrapping si realmente NO hay espacio disponible a la derecha
      // Usar spaceToEndAfterGroup que se recalcula después de la lógica de grupo contiguo
      // Verificar si realmente hay espacio disponible desde alongWall hasta el final de la pared
      // Solo entrar en wrapping si:
      // 1. El item no cabe al final de la pared (itemFitsAtEnd = false)
      // 2. O no hay suficiente espacio hasta el final (spaceToEndAfterGroup < itemWidth)
      // 3. O no hay espacio disponible en la posición actual Y no hay espacio disponible más adelante
      const hasSpaceAtCurrentPosition = checkSpaceAvailable(alongWall)
      // Verificar si hay espacio disponible más adelante (desde alongWall hasta el final)
      let hasSpaceAhead = false
      if (alongWall + itemWidth <= wallLength) {
        // Buscar espacio disponible desde alongWall hasta el final
        for (let checkPos = alongWall; checkPos <= wallLength - itemWidth; checkPos += 10) {
          if (checkSpaceAvailable(checkPos)) {
            hasSpaceAhead = true
            break
          }
        }
      }

      const shouldEnterWrappingLogic =
        !placedInContiguousGroup &&
        (!itemFitsAtEnd ||
          spaceToEndAfterGroup < itemWidth ||
          (!hasSpaceAtCurrentPosition && !hasSpaceAhead))

      // Guardar si realmente entró en el bloque de wrapping para usarlo después
      let enteredWrappingBlock = false
      // Guardar referencia al item de la izquierda para usar su posición directamente al calcular la posición final
      let leftReferenceItem: Item | LinkedItem | null = null
      let closestItemToLeft: { id: AnyID; endPosition: number } | null = null

      if (shouldEnterWrappingLogic) {
        enteredWrappingBlock = true
        // Obtener todos los muebles en la pared (excepto el actual)
        const allItemsOnWall = new Set([...wall.highItems, ...wall.lowItems])
        allItemsOnWall.delete(itemID)
        // Encontrar el mueble más cercano a la izquierda de la posición proyectada que tenga espacio después
        // Filtrar por tipo: MAL solo busca otros MAL, MBJ/COL solo buscan MBJ/COL
        const itemTipo = item.tipoNormalizado
        let bestPosition: number | null = null
        closestItemToLeft = null
        let minDistance = Infinity
        for (const otherItemID of allItemsOnWall) {
          const otherItem = items.get(otherItemID) as Item | LinkedItem
          if (!otherItem || !otherItem.snappedWall || otherItem.snappedWall !== wallID) continue

          // Filtrar por tipo: solo considerar items del mismo tipo para wrapping
          // MAL busca MAL y COL (porque MAL puede estar al lado de COL)
          // MBJ/COL solo buscan MBJ/COL
          if (
            itemTipo === 'MAL' &&
            otherItem.tipoNormalizado !== 'MAL' &&
            otherItem.tipoNormalizado !== 'COL'
          ) {
            continue // MAL busca MAL y COL
          }
          if (
            (itemTipo === 'MBJ' || itemTipo === 'COL') &&
            otherItem.tipoNormalizado !== 'MBJ' &&
            otherItem.tipoNormalizado !== 'COL'
          ) {
            continue // MBJ/COL solo buscan MBJ/COL
          }
          const otherItemWidthMorph = otherItem.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const otherItemWidth = otherItem.long?.toLowerCase().includes('encimera b3200')
            ? (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
            : otherItem.long !== 'Frontal' || otherItem.long?.toLowerCase().includes('zocalo')
              ? (otherItemWidthMorph?.default ?? otherItem.width ?? 0)
              : (otherItemWidthMorph?._default ?? otherItem.width ?? 0)
          // Calcular correctamente dónde termina el mueble a lo largo de la pared
          // Usar la misma lógica que areContiguous para calcular los vértices y obtener el máximo
          const otherItemPos = new Vector3()
          const otherItemQuat = new Quaternion()
          otherItem.matrix.decompose(otherItemPos, otherItemQuat, new Vector3())
          // Calcular los vértices del mueble usando la misma lógica que areContiguous
          const otherItemAngle = radians(get().getInitialAngleDegrees(otherItemID))
          const otherItemCos = Math.cos(otherItemAngle)
          const otherItemSin = Math.sin(otherItemAngle)
          // Calcular los vértices del mueble (igual que en areContiguous)
          const otherItemP = { x: otherItemPos.x, z: otherItemPos.z }
          const otherItemVertices = [
            { x: 0, z: 0 },
            { x: otherItemWidth, z: 0 },
            { x: otherItemWidth, z: (otherItem.thickness || 0) + (otherItem.regleta || 0) },
            { x: 0, z: (otherItem.thickness || 0) + (otherItem.regleta || 0) },
          ].map((vertex) => ({
            x: otherItemP.x + vertex.x * otherItemCos - vertex.z * otherItemSin,
            z: otherItemP.z + vertex.x * otherItemSin + vertex.z * otherItemCos,
          }))
          // Proyectar todos los vértices en la dirección de la pared y encontrar el máximo (donde termina)
          let otherItemMaxAlongWall = 0
          for (const vertex of otherItemVertices) {
            const vertexToStart = toVector3([vertex.x, 0, vertex.z]).sub(start)
            const vertexAlongWall = Math.max(0, vertexToStart.dot(wallDirNormalized))
            otherItemMaxAlongWall = Math.max(otherItemMaxAlongWall, vertexAlongWall)
          }
          // Usar el máximo sin ajuste para obtener la posición exacta donde termina
          const otherItemEnd = otherItemMaxAlongWall
          // Si el mueble está a la izquierda de la posición proyectada
          if (otherItemEnd <= alongWall) {
            const distance = alongWall - otherItemEnd
            // Verificar que haya espacio suficiente después de este mueble para colocar el nuevo
            // Usar otherItemEnd directamente (sin ajuste) para la verificación de espacio
            const positionAfterItem = otherItemEnd
            if (
              positionAfterItem + itemWidth <= wallLength &&
              checkSpaceAvailable(positionAfterItem)
            ) {
              if (distance < minDistance) {
                minDistance = distance
                // Guardar la posición exacta donde termina el mueble, pero ajustar ligeramente al colocar para eliminar el espacio
                closestItemToLeft = { id: otherItemID, endPosition: otherItemEnd }
                bestPosition = positionAfterItem
              }
            }
          }
        }
        // Si encontramos un mueble a la izquierda con espacio, colocar el nuevo mueble pegado a él
        if (bestPosition !== null && closestItemToLeft) {
          leftReferenceItem = items.get(closestItemToLeft.id) as Item | LinkedItem
          // Verificar espacio disponible JUSTO después del mueble
          const positionAfterItem = closestItemToLeft.endPosition
          const spaceCheckPosition = positionAfterItem
          if (checkSpaceAvailable(spaceCheckPosition)) {
            // Si hay espacio, colocar pegado exactamente donde termina el otro mueble
            // IMPORTANTE: Usar exactamente positionAfterItem sin redondeos ni ajustes
            // Esto asegura que el nuevo mueble quede pegado exactamente igual que en el grupo contiguo
            alongWall = positionAfterItem
          } else {
            // Buscar espacio disponible después del mueble
            let foundSpace = false
            let searchPosition = positionAfterItem + 10 // Comenzar 10mm después
            while (searchPosition <= wallLength - itemWidth && !foundSpace) {
              if (checkSpaceAvailable(searchPosition)) {
                alongWall = searchPosition
                foundSpace = true
              }
              searchPosition += 10
            }

            if (!foundSpace) {
              // Buscar desde el final hacia atrás
              alongWall = Math.max(0, wallLength - itemWidth)
            }
          }
          // Asegurarse de que el mueble quepa en la pared
          if (alongWall + itemWidth > wallLength) {
            alongWall = Math.max(0, wallLength - itemWidth)
          }
        } else {
          // No hay muebles del mismo tipo a la izquierda, buscar cualquier espacio disponible
          // Primero intentar desde el final hacia atrás
          let leftAlongWall = Math.max(0, wallLength - itemWidth)
          let foundSpace = false
          while (leftAlongWall >= 0 && !foundSpace) {
            if (checkSpaceAvailable(leftAlongWall)) {
              foundSpace = true
              alongWall = leftAlongWall
            } else {
              leftAlongWall -= 10
            }
          }
          // Si no se encontró espacio desde el final, buscar desde el inicio
          if (!foundSpace) {
            let searchPosition = 0
            while (searchPosition <= wallLength - itemWidth) {
              if (checkSpaceAvailable(searchPosition)) {
                alongWall = searchPosition
                foundSpace = true
                break
              }
              searchPosition += 10
            }
            // Si aún no hay espacio, usar la posición original (puede solaparse, pero es mejor que nada)
            if (!foundSpace) {
              alongWall = projectedVector.length()
              if (alongWall > wallLength - itemWidth) {
                alongWall = Math.max(0, wallLength - itemWidth)
              }
            }
          }
        }
        // **DESPUÉS de encontrar alongWall, FORZAR CONTIGÜIDAD**
        // Solo aplicar forceContiguity si NO se colocó en un grupo contiguo Y NO se está saltando la verificación
        // Y solo si NO entró en el bloque de wrapping (porque el wrapping ya encontró la posición correcta)
        // Si entró en wrapping, la posición ya fue ajustada correctamente y no necesita forceContiguity
        if (!placedInContiguousGroup && !skipContiguityCheck && !enteredWrappingBlock) {
          // **IMPORTANTE**: Solo aplicar forceContiguity si realmente NO hay espacio disponible a la derecha
          // Esto asegura que MBJ se comporte igual que MAL: solo buscar espacio a la izquierda cuando realmente no hay espacio a la derecha
          const hasSpaceRight =
            checkSpaceAvailable(alongWall) && alongWall + itemWidth <= wallLength
          if (!hasSpaceRight) {
            // No hay espacio disponible, aplicar forceContiguity para buscar contigüidad
            alongWall = forceContiguity(alongWall)
          }
          // Si hay espacio disponible, NO aplicar forceContiguity para evitar mover el item cuando ya está bien posicionado
        }
      }

      // Si preserveY es true, también preservar X/Z a menos que necesite wrapping
      // Esto asegura que cuando MAL está encima de MBJ, mantenga su posición relativa
      let finalX = itemPosition.x
      let finalZ = itemPosition.z
      let finalY = itemPosition.y

      if (preserveY) {
        // Si preserveY es true, solo ajustar posición si realmente necesita wrapping
        // (es decir, si entró en el bloque de wrapping)
        if (shouldEnterWrappingLogic) {
          // Necesita wrapping: ajustar X/Z pero preservar Y
          const adjustedPosition = start
            .clone()
            .add(wallDirNormalized.clone().multiplyScalar(alongWall))
          finalX = adjustedPosition.x
          finalZ = adjustedPosition.z
          finalY = itemPosition.y // Preservar Y original
        } else {
          // No necesita wrapping: preservar X/Z/Y originales
          finalX = itemPosition.x
          finalZ = itemPosition.z
          finalY = itemPosition.y
        }
      } else {
        // Comportamiento normal: calcular posición ajustada sobre la pared
        // Si se colocó a la izquierda de otro item, usar su posición directamente para mayor precisión
        let adjustedPosition: Vector3
        if (leftReferenceItem && closestItemToLeft) {
          // Calcular la posición directamente desde el item de referencia para mayor precisión
          // Esto asegura que MAL quede perfectamente pegado igual que MBJ
          const leftRefPos = new Vector3()
          const leftRefQuat = new Quaternion()
          leftReferenceItem.matrix.decompose(leftRefPos, leftRefQuat, new Vector3())
          const leftRefWidthMorph = leftReferenceItem.morphs?.find(
            (morph) => morph.name === 'Ancho' || morph.name === 'width',
          )
          const leftRefWidth = leftReferenceItem.long?.toLowerCase().includes('encimera b3200')
            ? (leftRefWidthMorph?._default ?? leftReferenceItem.width ?? 0)
            : leftReferenceItem.long !== 'Frontal' ||
                leftReferenceItem.long?.toLowerCase().includes('zocalo')
              ? (leftRefWidthMorph?.default ?? leftReferenceItem.width ?? 0)
              : (leftRefWidthMorph?._default ?? leftReferenceItem.width ?? 0)
          const leftRefAngle = radians(get().getInitialAngleDegrees(closestItemToLeft.id))
          const leftRefCos = Math.cos(leftRefAngle)
          const leftRefSin = Math.sin(leftRefAngle)
          // Calcular el vértice derecho del item de referencia (donde termina)
          const leftRefEndVertex = {
            x: leftRefPos.x + leftRefWidth * leftRefCos,
            z: leftRefPos.z + leftRefWidth * leftRefSin,
          }
          // Usar directamente este vértice como posición inicial del nuevo item
          adjustedPosition = toVector3([leftRefEndVertex.x, 0, leftRefEndVertex.z])
        } else {
          // Comportamiento normal: calcular desde alongWall
          adjustedPosition = start.clone().add(wallDirNormalized.clone().multiplyScalar(alongWall))
        }
        finalX = adjustedPosition.x
        finalZ = adjustedPosition.z

        // Aplicar restricción de altura en modo 3D
        const mode = store.mode
        if (mode === 'MODE_3D_VIEW') {
          // Restringir mínimo a 0 siempre
          finalY = Math.max(0, itemPosition.y)

          // Si el item tiene altura, aplicar restricción máxima
          if (item.height) {
            // Usar la altura de la pared a la que está pegado
            const maxWallHeight = wall.height

            // Restringir máximo considerando la altura del mueble
            const maxYAllowed = maxWallHeight - item.height
            finalY = Math.min(finalY, maxYAllowed)
          }
        } else {
          finalY = itemPosition.y
        }
      }

      // Calcular el centro del item para el bounding box
      const itemCenter = new Vector3(item.width / 2, item.height / 2 + finalY, item.thickness / 2)
      // Aplicar la rotación al centro del item
      itemCenter
        .applyQuaternion(toQuaternion().setFromEuler(new Euler(0, -rotation, 0, 'XYZ')))
        .add(new Vector3(finalX, 0, finalZ))
      // Convertir la rotación del quaternion a una Matrix4 y extraer la Matrix3
      const neweuler = new Euler(0, -rotation, 0, 'XYZ')
      const rotationMatrix4 = new Matrix4().makeRotationFromEuler(neweuler)
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
      if (length < 0) {
        length = 0
      }
      if (length > width || length + item.width > width) {
        length = width
      }
      if (!item) return

      item.matrix.makeRotationFromEuler(new Euler(0, -rotation, 0, 'XYZ'))
      if (!item.visible) item.visible = true
      item.matrix.setPosition(finalX, finalY, finalZ)
      item.x = finalX
      item.z = finalZ
      item.y = finalY
      // ItemGroups no deben tener snappedWall ni agregarse a las paredes
      if (item.type !== 'itemGroup') {
        item.snappedWall = wallID
      }
      // Si se colocó en un grupo contiguo, incrementar contador para forzar recálculo de texturas
      // SOLO si es MBJ (no para COL ni MAL)
      if (placedInContiguousGroup && item.tipoNormalizado === 'MBJ') {
        store.contadorModificaciones = (store.contadorModificaciones || 0) + 1
      }
      if (item.tipoNormalizado === 'MAL') {
        wall.highItems.add(itemID)
      }
      if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
        wall.lowItems.add(itemID)
      }
      store.sceneBoundingBoxes.set(itemID, {
        boundingBox: new OBB(
          itemCenter,
          toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
          rotationMatrix,
        ),
        type: 'items',
      })
      // Forzar actualización de texturas de encimera después de colocar el mueble
      // SOLO si es MBJ (no para COL ni MAL)
      if (item.tipoNormalizado === 'MBJ') {
        store.contadorModificaciones = store.contadorModificaciones + 1
      }
    })
  },
  drawItemGroup: async (idUnidadesAgrupadas: number, matrix?: Matrix4): Promise<AnyID | null> => {
    try {
      // 1. Obtener la unidad agrupada del backend
      const response = await getUnidadAgrupada(idUnidadesAgrupadas)

      // 2. Parsear el JSON con SuperJSON
      const parsed = SuperJSON.parse(response.escena.json)

      // La estructura puede ser parsed.json o parsed directamente
      const escenaData = (parsed.json || parsed) as {
        group: ItemGroup
        items: Record<string, Item | LinkedItem>
        linkedSons: Record<string, any>
      }

      const { group, items, linkedSons } = escenaData

      // 3. Crear mapeo de IDs antiguos a nuevos
      const idMapping = new Map<string, AnyID>()
      const drawnItems: AnyID[] = []

      // 4. Dibujar cada linkedItem usando drawItem (carga el GLB)
      // IMPORTANTE: Pasamos skipLinkedSons: true para evitar crear linkedSons automáticamente
      // Los linkedSons se dibujarán manualmente en el paso 5 con los datos guardados
      for (const [oldId, itemData] of Object.entries(items)) {
        const item = itemData as LinkedItem

        // Detectar el tipo original del item para generar el ID correcto
        const itemType = item.type === 'items' ? 'items' : 'linkedItem'

        // Usar catalogID para cargar el GLB, no idUnidades
        const newId = await get().drawItem(
          getId(itemType),
          item.catalogID || String(item.idUnidades),
          item.image || '',
          true,
          null,
          itemType,
          undefined, // realCatalogID
          false, // shouldGoToBackHeight
          true, // skipLinkedSons - NO crear linkedSons automáticamente
        )

        idMapping.set(oldId, newId)
        drawnItems.push(newId)

        // Reconstruir matriz desde JSON
        const itemMatrix = new Matrix4()
        if (item.matrix && (item.matrix as any).elements) {
          itemMatrix.fromArray((item.matrix as any).elements)
        }

        // Aplicar las propiedades guardadas con editItem
        get().editItem(newId, {
          matrix: itemMatrix,
          textures: item.textures,
          zones: item.zones,
          x: item.x,
          y: item.y,
          z: item.z,
          rotation: item.rotation,
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          selected: false,
          visible: true,
          type: itemType, // Preservar el tipo original
          childs: new Set(), // Inicializar como Set vacío, se actualizará en paso 6 si hay hijos
        } as any)

        // IMPORTANTE: Sincronizar morphs con las dimensiones restauradas
        if (item.width !== undefined && item.width > 0) {
          get().editMorph(newId, 'Ancho', item.width)
        }
        if (item.height !== undefined && item.height > 0) {
          get().editMorph(newId, 'Alto', item.height)
        }
        if (item.thickness !== undefined && item.thickness > 0) {
          get().editMorph(newId, 'Fondo', item.thickness)
        }
      }

      // 5. Dibujar cada linkedSon desde el JSON guardado
      if (linkedSons && Object.keys(linkedSons).length > 0) {
        for (const [oldSonId, sonData] of Object.entries(linkedSons)) {
          const son = sonData as any

          // Usar catalogID para cargar el GLB, no idUnidades
          const newSonId = await get().drawItem(
            getId('linkedSon'),
            son.catalogID || String(son.idUnidades),
            son.image || '',
            true,
            null,
            'linkedSon',
            undefined, // realCatalogID
            false, // shouldGoToBackHeight
            true, // skipLinkedSons - NO crear linkedSons anidados
          )

          idMapping.set(oldSonId, newSonId)

          // Reconstruir matriz
          const sonMatrix = new Matrix4()
          if (son.matrix && (son.matrix as any).elements) {
            sonMatrix.fromArray((son.matrix as any).elements)
          }

          // Actualizar firstParentID con el nuevo ID
          let newFirstParentID = son.firstParentID
          if (son.firstParentID) {
            const mappedParentId = idMapping.get(son.firstParentID)
            if (mappedParentId) {
              newFirstParentID = mappedParentId
            }
          }

          // Aplicar propiedades del hijo
          get().editItem(newSonId, {
            matrix: sonMatrix,
            textures: son.textures,
            zones: son.zones,
            x: son.x,
            y: son.y,
            z: son.z,
            rotation: son.rotation,
            width: son.width,
            height: son.height,
            thickness: son.thickness,
            firstParentID: newFirstParentID,
            depth: son.depth,
            selected: false,
            visible: true,
            type: 'linkedSon', // Asegurar que el tipo sea correcto
            childs: new Set(), // Inicializar como Set vacío
          } as any)

          // IMPORTANTE: Sincronizar morphs con las dimensiones restauradas para linkedSons
          if (son.width !== undefined && son.width > 0) {
            get().editMorph(newSonId, 'Ancho', son.width)
          }
          if (son.height !== undefined && son.height > 0) {
            get().editMorph(newSonId, 'Alto', son.height)
          }
          if (son.thickness !== undefined && son.thickness > 0) {
            get().editMorph(newSonId, 'Fondo', son.thickness)
          }
        }
      }

      // 6. Actualizar las referencias de childs en linkedItems
      for (const [oldId, itemData] of Object.entries(items)) {
        const item = itemData as LinkedItem
        const childCount =
          item.childs instanceof Set ? item.childs.size : (item.childs?.length ?? 0)
        if (item.childs && childCount > 0) {
          const newId = idMapping.get(oldId)
          if (newId) {
            const updatedChilds = new Set<string>()

            const childsArray = item.childs instanceof Set ? Array.from(item.childs) : item.childs
            childsArray.forEach((oldChildId) => {
              const newChildId = idMapping.get(oldChildId as string)
              if (newChildId) {
                updatedChilds.add(newChildId as string)
              }
            })

            // Actualizar childs del item
            get().editItem(newId, {
              childs: updatedChilds,
            } as any)
          }
        }
      }

      // 7. Crear el grupo con los nuevos IDs
      const newGroupId = getId('itemGroup') as ItemGroupID

      const newChildren = new Set<AnyID>()
      group.children.forEach((oldChildId) => {
        const newChildId = idMapping.get(oldChildId as string)
        if (newChildId) {
          newChildren.add(newChildId)
        }
      })

      // Reconstruir matriz del grupo
      const groupMatrix = new Matrix4()
      if (group.matrix && (group.matrix as any).elements) {
        groupMatrix.fromArray((group.matrix as any).elements)
      }

      // Usar la matriz proporcionada o la del grupo
      const finalMatrix = matrix ? matrix.clone() : groupMatrix

      // Extraer posición
      const position = new Vector3()
      const quaternion = new Quaternion()
      const scale = new Vector3()
      finalMatrix.decompose(position, quaternion, scale)

      // Crear el grupo en el store
      set((store) => {
        const newGroup: ItemGroup = {
          ...group,
          id: newGroupId,
          children: newChildren,
          matrix: finalMatrix,
          x: position.x,
          y: position.y,
          z: position.z,
          selected: true,
          visible: true,
          canCollide: true,
          idUnidadesAgrupadas: idUnidadesAgrupadas, // Guardar el ID del backend
        }

        store.items.set(newGroupId, newGroup as any)
        store.layers[store.currentLayer].items.add(newGroupId)

        // IMPORTANTE: Transformar items hijos al espacio local del grupo
        const groupInverse = finalMatrix.clone().invert()
        newChildren.forEach((childId) => {
          const child = store.items.get(childId)
          if (child && child.matrix) {
            // Convertir matriz de mundo a matriz local respecto al grupo
            const localMatrix = groupInverse.clone().multiply(child.matrix)

            const pos = new Vector3()
            const rot = new Quaternion()
            const scale = new Vector3()
            localMatrix.decompose(pos, rot, scale)

            // Actualizar el hijo con su matriz local
            ;(child as any).matrix = localMatrix
            ;(child as any).x = pos.x
            ;(child as any).y = pos.y
            ;(child as any).z = pos.z
            ;(child as any).isBlocked = true
          }
        })

        // Mover el grupo al origen (0,0,0) manteniendo su rotación
        const newPosition = new Vector3(0, 0, 0)
        const matrixAt000 = new Matrix4().compose(newPosition, quaternion, scale)

        // Actualizar la matriz y posición del grupo
        newGroup.matrix = matrixAt000
        newGroup.x = 0
        newGroup.y = 0
        newGroup.z = 0

        // Computar y añadir OBB del grupo con la nueva matriz en 0,0,0
        const groupOBBResult = computeItemGroupOBB(newChildren, store.items, matrixAt000)
        if (groupOBBResult) {
          const { center, halfSize, rotation } = groupOBBResult
          const groupOBB = new OBB(center, halfSize, rotation)
          store.sceneBoundingBoxes.set(newGroupId, {
            boundingBox: groupOBB,
            type: 'items',
          })
        }

        // Eliminar OBBs individuales de todos los items dibujados (linkedItems y linkedSons)
        // Solo debe quedar la OBB del grupo
        idMapping.forEach((newId) => {
          store.sceneBoundingBoxes.delete(newId)
        })

        // También eliminar la OBB del grupo antiguo si existe
        if (group.id && store.sceneBoundingBoxes.has(group.id as AnyID)) {
          store.sceneBoundingBoxes.delete(group.id as AnyID)
        }

        // Eliminar items individuales de collisionableItems
        // Solo el grupo debe poder colisionar
        idMapping.forEach((newId) => {
          store.collisionableItems.delete(newId)
        })

        // Añadir a collisionables solo el grupo
        store.collisionableItems.add(newGroupId)

        // Seleccionar el grupo
        store.currentObjectID = newGroupId
        store.layers[store.currentLayer].selected.clear()
        store.layers[store.currentLayer].selected.add(newGroupId)
      })

      return newGroupId
    } catch (error) {
      console.error('[drawItemGroup] ❌ Error:', error)
      return null
    }
  },
  editZone: async (id: AnyID, zoneToPasteTo: string, _copiedMaterial: Zone[string]['material']) => {
    set((store) => {
      const sceneObject = store.items.get(id) ?? store.areas.get(id)
      if (!sceneObject)
        return console.log('No scene object found on editting zone', { ...sceneObject })

      const textureOfTheZoneToPasteTo = sceneObject.textures.find(
        (el) => el.zoneName === zoneToPasteTo,
      )
      if (!textureOfTheZoneToPasteTo) {
        return
      }
      if (!_copiedMaterial) return console.log('Not material found', { _copiedMaterial })
      sceneObject.zones[zoneToPasteTo].material = { ..._copiedMaterial }
      textureOfTheZoneToPasteTo.groupID = _copiedMaterial?.groupID
      textureOfTheZoneToPasteTo.name = _copiedMaterial?.name
      textureOfTheZoneToPasteTo.searchID = _copiedMaterial?.searchID
      textureOfTheZoneToPasteTo.url = _copiedMaterial?.url
      textureOfTheZoneToPasteTo.id = _copiedMaterial?.id
      textureOfTheZoneToPasteTo.modelo = _copiedMaterial?.modelo
      textureOfTheZoneToPasteTo.acabado = _copiedMaterial?.acabado
    })
  },
  toggleVisibility: (elementID: string) => {
    set((store) => {
      const item = store.items.get(elementID) ?? store.areas.get(elementID)
      if (item) {
        item.visible = !item.visible
      }
    })
  },
  setExclusiveVisibility: (idsToKeepVisible: Set<string>, hideDimentions3D: boolean) => {
    set((store) => {
      store.items.forEach((item, id) => {
        if (item.type === 'lights') return
        const isVisible = idsToKeepVisible.has(id)
        item.visible = isVisible
        if (hideDimentions3D) {
          if (item.type === 'walls' || item.type === 'partition' || item.type === 'hiddenWall') {
            item.wallVisible = isVisible
          }
        }
      })
      store.areas.forEach((area, id) => {
        area.visible = idsToKeepVisible.has(id)
      })
    })
  },
  toggleCollision: (id: string) => {
    set((store) => {
      const item = store.items.get(id)
      if (!item || !id) return console.log('Cant find item on collision')
      item.canCollide = !item?.canCollide
      const collisionableItems = store.collisionableItems
      if (!item.canCollide) {
        collisionableItems.delete(id)
      } else {
        collisionableItems.add(id)
      }
    })
  },
  toggleIsBlocked: (selectedItems: Set<string>) => {
    set((store) => {
      selectedItems.forEach((id) => {
        const item = store.items.get(id)
        if (!item || !id) return console.log('Cant find item on blocking')
        item.isBlocked = !item?.isBlocked
      })
    })
  },
  // TODO: esto probablemente sobra con lo de rafa
  setShouldUpdateCamera: (val: boolean) => {
    set((store) => {
      store.shouldUpdateCamera = val
    })
  },
  // TODO: esto probablemente sobra con lo de rafa

  setCameraPosition(y: number | string) {
    set((store) => {
      store.camera2DPosition.zoom = parseInt(y) / 1000
    })
  },
  setFrameloop: (frameloop: Store['frameloop']) => {
    set((store) => {
      store.frameloop = frameloop
    })
  },
  // TODO: esto probablemente sobra o hay que rehacerlo
  addIncrement: () => {
    set((s) => {
      s.increment = s.increment + 1
    })
  },
  addContadorModificaciones: () => {
    set((s) => {
      s.contadorModificaciones = s.contadorModificaciones + 1
    })
  },
})
